/**
 * English messages.
 *
 * English has two plural categories, so only `_one` and `_other` appear here.
 * i18next picks the right suffix per locale, which is why the Polish catalogue
 * carries four for the same keys — `pnpm i18n:check` accounts for that rather
 * than demanding the two files have identical key sets.
 */
export const en = {
  app: {
    name: 'Overseer',
    tagline: 'Working time and attendance',
  },
  access: {
    historySince: 'Data from {{date}}.',
  },
  nav: {
    label: 'Main navigation',
    dashboard: 'Dashboard',
    timesheet: 'Timesheet',
    employees: 'Employees',
    workLogs: 'Card reads',
    daysOff: 'Absences',
    cardReaders: 'Card readers',
    referenceData: 'Reference',
    payroll: 'Payroll',
    reports: 'Reports',
    users: 'Users',
    roles: 'Roles',
    companies: 'Companies',
    sources: 'Data sources',
    logs: 'Logs',
  },
  auth: {
    signInTitle: 'Sign in',
    signInSubtitle: 'Use your email address and password',
    email: 'Email address',
    password: 'Password',
    newPassword: 'New password',
    confirmNewPassword: 'Repeat new password',
    signIn: 'Sign in',
    signOut: 'Sign out',
    signingIn: 'Signing in…',
    newPasswordRequired: 'Set a new password',
    newPasswordRequiredHint: 'This is the first sign-in for this account. Choose your own password.',
    setPassword: 'Set password',
    passwordsDoNotMatch: 'The passwords do not match',
    errors: {
      credentials: 'Incorrect email address or password.',
      passwordPolicy: 'That password does not meet the requirements.',
      tooManyRequests: 'Too many attempts. Wait a moment.',
      network: 'Cannot reach the authentication service.',
      unknown: 'Could not sign in. Try again.',
    },
  },
  dashboard: {
    title: 'Dashboard',
    asOf: 'As of {{time}}',
    inNow: 'Currently at work',
    inNowEmpty: 'Nobody is at work right now.',
    closedToday: 'Finished today',
    staleOpen: 'Unclosed days',
    staleOpenEmpty: 'No unclosed days.',
    staleOpenCorrectHint:
      'Days with an entry but no exit, from earlier dates. Select a row to correct it.',
    inactive: {
      title: 'Potentially inactive',
      description: 'No hours, absences or pay between {{from}} and {{to}}, and nothing since.',
      empty: 'Everybody on the books has been active recently.',
      lastWorkDay: 'Last work day',
      lastPaid: 'Last paid',
      deactivate: 'Deactivate',
      deactivated: '{{name}} has been deactivated.',
      confirmTitle: 'Deactivate {{name}}?',
      confirmWithAgreement:
        'They will be marked inactive and their current agreement will be closed on the date below.',
      confirmNoAgreement:
        'They will be marked inactive. They have no agreement in force, so nothing else changes.',
      endDate: 'Agreement end date',
      endDateHint: 'The last day the agreement runs. It cannot be before the agreement started.',
    },
    employee: 'Employee',
    since: 'Since',
    elapsed: 'Elapsed',
    date: 'Date',
    start: 'In',
    end: 'Out',
    worked: 'Worked',
    closedTodayEmpty: 'Nobody has finished a shift today yet.',
    peopleIn_one: '{{count}} person at work',
    peopleIn_other: '{{count}} people at work',
    peopleFinished_one: '{{count}} person has finished',
    peopleFinished_other: '{{count}} people have finished',
  },
  reports: {
    title: 'Reports',
    subtitle: 'Employee cost and headcount by department. Closed months from the payroll, the next 12 months as a forecast.',
    subtitleHeadcount: 'Headcount by department. Closed months from the payroll, the next 12 months as a forecast.',
    notAvailable: 'You do not have access to the HR reports.',
    period: 'Report period',
    previousYear: 'Previous year',
    nextYear: 'Next year',
    range: {
      label: 'Range',
      month: 'Month',
      ytd: 'Year to date',
    },
    monthPicker: 'Month',
    monthState: {
      closed: '{{month}} — closed, from the payroll',
      forecast: '{{month}} — forecast',
      forecastChecked: '{{month}} — forecast; the open payroll has {{checked}} of {{people}} people checked',
      beyond: '{{month}} — past the forecast horizon',
    },
    noClosedMonth: 'No month of {{year}} is closed or forecast.',
    unassigned: 'No department',
    mom: 'MoM',
    yoy: 'YoY',
    comparedWith: 'against: {{period}}',
    comparedWithClosed: 'against: {{period}} — the last closed month',
    versus: 'vs {{month}}',
    comparedYearAgo: 'against the same period a year earlier',
    noComparison: 'The month before is not closed — no comparison.',
    yoyMissing: 'The same period a year earlier is not closed on the payroll — no comparison.',
    tiles: {
      cost: 'Total cost · {{period}}',
      people: 'People on the payroll',
      peopleAverage: 'People per month, average',
      perHead: 'Cost per person',
      perHeadMonth: 'Cost per person / month',
      budget: 'Budget · {{period}}',
      budgetNote: 'A cost target — to be set soon',
      budgetTarget: 'Target',
      budgetUsed: 'Used',
      soon: 'Soon',
      monthly: 'Monthly average',
      payA: 'Pay A',
      payB: 'Pay B',
      joined: 'Joined',
      left: 'Left',
      formsA: 'UoP · Zlec.',
      formsB: 'B2B · Other',
      distinct: 'Different people',
      months: 'Months',
      forecastOf: 'of which forecast',
    },
    forecast: {
      chip: 'Forecast',
      chipMixed: 'With forecast',
      title: 'Forecast — {{period}}',
      titleMixed: 'The period includes a forecast — {{months}}',
      text: 'An estimate, not the payroll. The figures will change once the month is closed.',
      textMixed: '{{closed}} from the payroll, {{forecast}} an estimate. The total will change once those months are closed.',
      method:
        'Each person with an agreement in force: their pay over the last closed months, moved by their agreement\'s rate, the days of the month and the season from last year\'s timesheet.',
      base: 'Base: {{months}} from the payroll',
      checked: '{{month}}: {{checked}} of {{people}} people already checked on the payroll',
      horizon: 'Horizon: up to {{month}}',
      band: 'Possible error: ±{{percent}}',
      range: 'Range: {{low}} – {{high}}',
      rangeTitle: 'The forecast part may be off by this much either way — the largest error in the test on closed months.',
      split: '{{paid}} closed · ≈ {{forecast}} forecast',
      about: 'How the forecast is made',
      checkedShort: '{{checked}} of {{people}} checked',
    },
    table: {
      title: 'Departments',
      subtitle: '{{period}} · click a department to see its people by position',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      about: 'What the table counts',
      fullScreen: 'Full screen',
      hoursChange: 'Hours the month before, absences included: {{hours}}',
      cost: 'Cost',
      people: 'People',
      department: 'Department',
      total: 'Total',
      payA: 'Pay A',
      payB: 'Pay B',
      headcount: 'People',
      headcountAverage: 'Avg people',
      perHead: 'Per person',
      company: 'Whole company',
      noPosition: 'No position',
      hours: 'Hours worked, from the timesheet',
      hoursForecast: 'Hours — forecast',
      hoursWithAbsence: 'Worked {{worked}} and absent {{absent}}, from the timesheet',
      absenceTitle: 'Absence — counted in the hours',
      absence: {
        holiday: 'leave {{hours}}',
        sick: 'sick {{hours}}',
        care: 'care {{hours}}',
      },
      checkedTitle: 'Row checked on the open payroll — the amount is the payroll\'s, not a forecast',
      fromAgreement: 'from agreement',
      fromAgreementTitle: 'Not paid yet — the amount is from the agreement and the department',
      new: 'new',
      movedFrom: 'from: {{department}}',
      monthsOf: '{{count}} of {{total}} mo.',
      movedTo: 'to: {{department}}',
      left: 'left',
      leftPaid: 'A month earlier: {{amount}}',
      splitOthers_one: '+{{count}} department',
      splitOthers_other: '+{{count}} departments',
      splitTitle: 'Split between departments: {{parts}}',
      splitHidden: 'other departments',
    },
    footnote:
      'People — those the closed month\'s payroll paid more than 0 zł. Pay A: employer cost less reimbursements, with invoices; Pay B: Pay B, Pay B bonus and Service Charge.',
    footnoteHeadcount: 'People — those the closed month\'s payroll paid more than 0 zł.',
    footnoteForecast:
      'In a forecast, people are those whose recent months say they will be paid; anyone already checked on the open payroll has the payroll\'s amount.',
  },
  timesheet: {
    employee: 'Employee',
    total: 'Total',
    dayHours: 'Hours per day',
    empty: 'No records in this month for the selected filters.',
    today: 'Today',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    showCosts: 'Show costs',
    showHours: 'Show hours',
    // The sheet carries hours only, which is why the label does not promise a
    // report — an "Export" that quietly left the money out would read as broken
    // to whoever is allowed to see it.
    export: {
      open: 'Export hours',
      running: 'Preparing…',
    },
    // The same month, in money. Shown only to whoever may manage payroll.
    openPayroll: 'Payroll',
    sortBy: 'Sort',
    // Two options where there was one called "Name", which sorted by forename.
    sortLastName: 'Surname',
    sortFirstName: 'First name',
    sortHours: 'Hours',
    sortWarnings: 'Needs attention',
    /** The timesheet's filter: whether anything records this person's attendance. */
    countsHours: 'Hours',
    hoursCounted: 'Recorded',
    hoursNotCounted: 'Not recorded',
    clearFilters: 'Clear',
    company: 'Company',
    department: 'Department',
    position: 'Position',
    all: 'All',
    positionUnavailable: '{{name}} (not in this department)',
    inactive: 'No longer employed',
    gridLabel: 'Monthly timesheet — employees in rows, days in columns',
    noRecord: 'No record',
    addRecord: 'No record — click to add one',
    /*
     * The month has been closed by payroll. Each message names what is no longer
     * possible *and* the way back, because a screen full of dead controls with no
     * next step is what gets reported as a fault.
     */
    lock: {
      closed: 'This month is closed — records are read-only. Reopen the month to change anything.',
      closedBy:
        'This month was closed by {{name}} — records are read-only. Reopen the month to change anything.',
      panelReadOnly: 'The month is closed. This day cannot be edited or deleted.',
      panelEnded: 'The month has ended. Only an HR administrator may correct this day now.',
    },
    absenceRange: '{{from}} to {{to}}',
    summary: {
      hours: 'Hours',
      daysWorked: 'Days worked',
      daysOff: 'Absences',
    },
    totals: {
      worked: 'Worked',
      /**
       * The single summary column's header: hours worked and absences in days.
       *
       * The four columns were collapsed into one, so `workedShort` alone no longer
       * describes what sits under the heading. Both figures are named in full on
       * the tooltip.
       */
      summaryShort: 'W / A',
      /** Absences in days, under the hours in the summary column. */
      dayCount_one: '{{count}} day',
      dayCount_other: '{{count}} days',
      /**
       * The day-total bar's tooltip. `hours` is the gap, `average` is the
       * mean the bar is drawn against — both `H:MM`, same as everything else
       * on this screen.
       */
      aboveAverage: '{{hours}} above the month average of {{average}}',
      belowAverage: '{{hours}} below the month average of {{average}}',
      atAverage: 'at the month average of {{average}}',
    },
    costs: {
      title: 'Costs',
      gross: 'Gross',
      employerCost: 'Employer cost',
      toDate: 'Through day {{day}}',
      forecast: 'Month forecast',
      byDepartment: 'By department',
      source: {
        estimate: 'Estimated from agreements and recorded hours. The month is not closed.',
        payroll: 'From the HR system. The month is closed.',
      },
    },
    type: {
      auto: 'From reader',
      manual: 'Entered by hand',
      holiday: 'Holiday',
      sick: 'Sick leave',
      care: 'Carer leave',
    },
    typeShort: {
      auto: 'A',
      manual: 'M',
      holiday: 'H',
      sick: 'S',
      care: 'C',
    },
    logType: {
      entry: 'in',
      exit: 'out',
    },
    panel: {
      close: 'Close',
      start: 'In',
      end: 'Out',
      duration: 'Worked',
      startDate: 'From',
      endDate: 'To',
      type: 'Type',
      /**
       * The type is not a choice — the server sets it. These say where it came
       * from, without which the field shows a word and no meaning.
       */
      typeHint: {
        auto: 'Derived from the card reads. A month rebuild may change or remove it.',
        manual: 'Entered by hand. A month rebuild leaves it alone.',
      },
      absence: 'Absence',
      workLocked: 'To record worked hours, delete this absence and add a work day.',
      /**
       * The kind of record the panel is editing, as a heading.
       *
       * The two modes no longer share their fields, so which one is open has to
       * be stated: an absence with no time inputs and a worked day with no
       * absence buttons look like the same form missing different halves.
       */
      modeWork: 'Worked hours',
      modeAbsence: 'Absence',
      /** The create dialog's two tabs. Same two kinds, chosen rather than shown. */
      tabWork: 'Worked hours',
      tabAbsence: 'Absence',
      saved: 'Work day saved',
      absenceSaved: 'Absence saved',
      created: 'Work day added',
      absenceCreated: 'Absence added',
      deleted: 'Work day deleted',
      missingAbsenceType: 'Choose the kind of absence.',
      description: 'Note',
      noEnd: 'none',
      save: 'Save',
      saving: 'Saving…',
      delete: 'Delete',
      confirmDelete: 'Delete this work day? This cannot be undone.',
      history: 'Change history',
      noHistory: 'No changes recorded.',
      auditedFrom: 'Changes have been recorded since {{date}}.',
      auditingNotStarted: 'No changes have been recorded yet.',
      workLogs: 'Card reads',
      noWorkLogs: 'No card reads — this day was entered by hand.',
      opens: 'opens the day',
      closes: 'closes the day',
      badTime: 'A time must look like HH:MM.',
      badDates: 'The end date cannot fall before the start date.',
      missingDate: 'Give the absence a start and an end date.',
      nextDay: '+1 day',
      nextDayHint: 'The exit falls on the following day — a night shift.',
      action: {
        create: 'Created',
        update: 'Changed',
        delete: 'Deleted',
      },
    },
  },
  /*
   * Payroll.
   *
   * `field` is the column heading, `fieldLong` the full name behind the tooltip and
   * the accessible name — real words in each language, not the code's own field
   * names, which are not headings in the HR spreadsheet and not words in either
   * language.
   *
   * A column is 116px, so anything longer than roughly thirteen characters is
   * abbreviated and the full phrase lives in `fieldLong`. `FP`, `FGŚP` and `ZUS`
   * stay as they are in both catalogues — they are the statutory names of Polish
   * funds, and an English rendering of `FGŚP` would name nothing at all.
   */
  payroll: {
    employee: 'Employee',
    totals: 'Total',
    empty: 'No payroll rows for this month. Import a file from HR.',
    emptyFiltered: 'No row matches the current filters.',
    filteredCount: 'Showing {{shown}} of {{total}}',
    clearFilters: 'Clear filters',
    scopedNote: 'You see the rows of your departments. The totals and the ready count cover only them.',
    companyHint: 'Payslip for {{company}} — this employee has more than one row this month.',
    sort: {
      label: 'Sort',
      lastName: 'Surname A\u2013Z',
      lastNameDesc: 'Surname Z\u2013A',
      firstName: 'First name A\u2013Z',
      firstNameDesc: 'First name Z\u2013A',
      total: 'Total, lowest first',
      totalDesc: 'Total, highest first',
      ready: 'Not ready first',
      readyDesc: 'Ready first',
    },
    today: 'Today',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    openTimesheet: 'Timesheet',
    ready: 'Ready',
    readyLong: 'Row checked by a person',
    rowStatus: {
      todo: 'To do',
      imported: 'From import',
      approved: 'Approved',
    },
    norm: 'Norm: {{hours}} h',
    normLong: "This month's statutory working-time norm — Art. 130 of the Labour Code",
    readyFor: 'Mark as checked: {{name}}',
    payment: {
      column: 'Payment',
      long: 'How the net pay goes out: bank transfer, or cash from the company’s stash A. Choose before marking ready — only ready sends a cash payout to the cash desk. A new row takes the form from the month before.',
      transfer: 'Transfer',
      cash: 'Cash',
      for: 'Payment: {{name}}',
    },
    tableLabel: 'Payroll for the month — employees in rows, figures in columns',
    cellLabel: '{{field}} — {{name}}',
    diverged: '{{field}} — {{name}}: edited by hand. The import said {{imported}}.',
    bailiffHint: 'Bailiff seizure of {{amount}} — net is the full amount; the bailiff takes that cut.',
    sickBenefitHint: 'ZUS benefit of {{amount}} — paid out by the company and reimbursed by ZUS, so it is taken off the employer cost.',
    rentedEmployeeHint: 'Rented out at {{margin}} — the renting company pays the cost back plus the margin ({{amount}}), so this row\u2019s total is normally below zero.',
    field: {
      grossPay: 'Gross',
      socialBase: 'Base',
      pensionEmployer: 'Pension',
      disabilityEmployer: 'Disability',
      accidentEmployer: 'Accident',
      labourFund: 'FP',
      guaranteedBenefitsFund: 'FGŚP',
      contributionsTotal: 'ZUS total',
      employerCost: 'Employer cost',
      // The sign is in the heading: a column of positive numbers that reduces the
      // total is the easiest figure on this screen to read backwards.
      benefitsReimbursed: 'Benefits (−)',
      invoices: 'Invoices',
      rentedMargin: 'Margin',
      rentedReimbursement: 'Reimbursed by renting company',
      payB: 'Pay B',
      payBBonus: 'Bonus B',
      serviceCharge: 'Service Charge',
      hoursWorkedPayroll: 'Hours (HR)',
      hoursWorkedTimesheet: 'Hours (sheet)',
      payrollTotal: 'Total',
      netPay: 'Net',
      netCash: 'Cash',
      netTransfer: 'Transfer',
      pensionEmployee: 'Pension (emp.)',
      disabilityEmployee: 'Disability (emp.)',
      sicknessEmployee: 'Sickness',
      healthContribution: 'Health',
      taxAdvance: 'Tax advance',
      taxDeductibleCosts: 'Tax costs',
      taxRelief: 'Tax relief',
      taxRelinquished: 'Tax given up',
    },
    payBLock: 'Lock Pay B',
    payBUnlock: 'Unlock Pay B',
    invoicesLock: 'Lock Invoices',
    invoicesUnlock: 'Unlock Invoices',
    fieldLong: {
      grossPay: 'Gross pay',
      // A base, not an amount — which is why it has no footer total.
      socialBase: 'Social insurance base — what contributions are computed on, not summed',
      pensionEmployer: 'Employer pension contribution',
      disabilityEmployer: 'Employer disability contribution',
      accidentEmployer: 'Accident insurance contribution',
      labourFund: 'Labour Fund (Fundusz Pracy)',
      guaranteedBenefitsFund: 'Guaranteed Employee Benefits Fund (FGŚP)',
      contributionsTotal: 'Social contributions, total — pension, disability, accident, FP and FGŚP',
      employerCost: 'Employer cost — gross plus contributions',
      benefitsReimbursed: 'Benefits — a reimbursement, subtracts from the total',
      invoices: 'B2B invoices',
      rentedMargin:
        'Margin on a rented employee — another company reimburses their whole cost plus this percent. Empty means not rented.',
      payB: 'B pay — the company’s own figure',
      payBBonus: 'B pay bonus — the company’s own figure',
      serviceCharge: 'Service Charge — the company’s own figure',
      hoursWorkedPayroll: 'Hours worked, per HR import',
      hoursWorkedTimesheet: 'Hours worked, from the timesheet, rounded to whole hours',
      payrollTotal: 'Total pay — derived, not editable',
      netPay: 'Net pay — what the person takes home',
      netCash: 'Net paid in cash (GOT)',
      netTransfer: 'Net paid to the bank (ROR)',
      pensionEmployee: 'Employee pension contribution',
      disabilityEmployee: 'Employee disability contribution',
      sicknessEmployee: 'Employee sickness contribution — a dash means it does not apply',
      healthContribution: 'Health insurance contribution',
      taxAdvance: 'Income-tax advance',
      taxDeductibleCosts: 'Tax-deductible costs',
      taxRelief: 'Tax relief',
      taxRelinquished: 'Tax given up (zan. pod.)',
    },
    /** Headings for the bands that group the money columns. */
    /**
     * The contract groups the rows are arranged into.
     *
     * The group's own name is `agreements.form.*`, reused rather than restated: a
     * second spelling of UoP would be a second thing to keep in step with the
     * employee screen.
     */
    group: {
      count_one: '{{count}} row',
      count_other: '{{count}} rows',
      collapse: 'Collapse the {{form}} group',
      expand: 'Expand the {{form}} group',
      subtotal: '{{form}} subtotal',
    },
    /** The agreement form beside the name. A B2B row reads as unpaid without it. */
    agreementFormEnded: 'Agreement: {{form}} — not in force this month',
    status: {
      open: 'Open',
      closed: 'Closed',
    },
    summary: {
      label: 'Month summary',
      netPay: 'Net pay',
      taxAdvance: 'Tax (US)',
      contributions: 'Contributions (ZUS)',
      invoices: 'B2B invoices',
      rentedReimbursement: 'Rented reimbursement',
      payB: 'Pay B',
      payBBonus: 'Pay B bonus',
      serviceCharge: 'Service Charge',
      ready: 'Checked rows',
      readyOf: '{{ready}} of {{active}}',
    },
    /** Opening a row's derivation. Named per row: a column of "expand" says nothing. */
    expand: 'Show the payslip for {{name}}',
    collapse: 'Hide the payslip for {{name}}',
    expandColumn: 'Payslip',
    /** The pencil beside a name: this row was typed over the import. */
    overridden: 'Edited by hand: {{fields}}',
    payslip: {
      label: 'Payslip — {{name}}',
      contributions: 'Employer contributions',
      cost: 'Cost',
      // Not plain "Hours": the card would carry the same name as its only figure.
      hours: 'Time worked',
      norm: 'Monthly norm',
      /** Why this person's norm is not the month's — a part-time post, a 7-hour day, or both. */
      normBasis: 'Scaled: {{etat}} etat',
      normBasis_disabled: 'Scaled: {{etat}} etat, 7-hour day',
      normDiff: 'Against the norm',
      imported: 'Imported {{when}}',
      notImported: 'Not imported',
      employeeZus: 'Employee contributions',
      tax: 'Tax and net',
      elements: 'Pay elements',
      payB: 'Pay B',
      payBSuggested: 'Suggestion',
      payBLocked: 'Amount locked — it will not refresh on reload.',
      rented: 'Rented',
      rentedBase: 'Base for reimbursement',
      rentedExplain:
        'A cost of {{base}} plus a {{margin}} margin comes to {{reimbursement}}. The renting company pays that back, which is why the Invoices column is negative — it is revenue, not cost.',
      rentedSource:
        'The margin is set on the employee. Changing it applies to every open month; a closed month keeps the value it was settled with.',
      payBExplain: {
        none: 'No Pay B schema and no hours above the norm — suggestion is 0.',
        fixed_compensation_month:
          'Fixed monthly compensation: {{value}} − net {{netPay}} = {{amount}}.',
        fixed_bonus_month: 'Fixed monthly bonus: {{amount}}.',
        fixed_compensation_hour:
          'Fixed hourly compensation: {{value}} × {{hours}} h = {{target}}, minus net {{netPay}} = {{amount}}.',
        /** Shown before the multiplication when absences make the hours differ from time worked. */
        hoursFrom:
          'Hours paid for: worked {{worked}} h + holiday {{holidayDays}} × 8 h = {{holidayHours}} h + sick/care {{sickCareDays}} × 8 h × 80% = {{sickCareHours}} h, total {{total}} h.',
        overtime:
          'Above the norm ({{hours}} h − norm {{monthNorm}} h = {{excess}} h) × hourly rate {{hourlyRate}} (from the agreement, {{rateUnit}}) = {{amount}}.',
      },
      /**
       * The Pay B calculation, one term per row.
       *
       * Replaces the prose in `payBExplain` for the three kinds that are actually
       * arithmetic. Those sentences are kept: `payBExplanationText` is still the
       * fallback for a fixed bonus and for no schema at all, where a table would
       * be a heading over one row.
       */
      payBWorking: {
        worked: 'Hours worked',
        holiday: 'Holiday ({{days}} × 8 h)',
        sickCare: 'Sick / care ({{days}} × 8 h × 80%)',
        hoursTotal: 'Hours paid for',
        rate: 'Hourly compensation',
        product: '{{rate}} × {{hours}}',
        target: 'Target amount',
        netPay: 'Net pay',
        result: 'Pay B',
        hoursAgainstNorm: 'Hours worked (norm {{norm}})',
        excess: 'Above the norm',
        hourlyRate: 'Hourly rate',
      },
    },
    hours: {
      /** Worked minus what HR paid for. Both sides are time at work, so this is overtime. */
      overtime: 'Overtime (RCP − HR)',
      holiday_one: 'Holiday ({{count}} day)',
      holiday_other: 'Holiday ({{count}} days)',
      sickCare_one: 'Sick / care ({{count}} day × 80%)',
      sickCare_other: 'Sick / care ({{count}} days × 80%)',
      payable: 'Hours paid for',
      /** The weekend and public-holiday days a leave spanned but nobody pays for. */
      skipped_one: 'Not counted: {{count}} day falling on a weekend or public holiday.',
      skipped_other: 'Not counted: {{count}} days falling on weekends or public holidays.',
      cellLabel:
        '{{name}}: timesheet {{timesheet}} h, HR {{imported}} h, difference {{diff}}',
      timesheetOnly: '{{name}}: timesheet {{timesheet}} h',
    },
    normMark: {
      disabled: '7 h',
      hint: 'This person owes {{norm}} h this month, not the standard figure — {{basis}}.',
    },
    lock: {
      closed: 'This month is closed — the figures are read-only. Reopen the month to change anything.',
      closedBy:
        '{{name}} closed this month — the figures are read-only. Reopen the month to change anything.',
      refused: 'The month was closed in the meantime. Your change was not saved.',
      entryRefused:
        'This row is approved, so its figures are read-only. Withdraw the approval to change anything.',
    },
    export: {
      open: 'Export',
      running: 'Preparing…',
    },
    import: {
      open: 'Import from HR',
      title: 'Import payroll — {{month}}',
      what: 'Load a PDF list or a sheet from the HR system. Nothing is saved until you confirm.',
      file: 'File from HR',
      fileHint: 'Optima PDF, or XLSX / CSV, up to 2 MB.',
      reading: 'Reading the file…',
      closed: 'This month is closed — the preview works, saving does not. Reopen the month first.',
      matched_one: 'Matched: {{count}} row',
      matched_other: 'Matched: {{count}} rows',
      matchedNote: 'These rows are written when you confirm. Change the person or discard a row if the match is wrong.',
      matchedEmpty: 'No row in the file matched an employee.',
      locked_one: 'Approved, not imported: {{count}} row',
      locked_other: 'Approved, not imported: {{count}} rows',
      lockedNote:
        'These people already have an approved row for this month. The import leaves them exactly as they are. Withdraw the approval on the row first if you want the new figures.',
      lockedEmpty: 'No approved rows in the way.',
      lockedDone_one: '{{count}} row was left alone because it is approved.',
      lockedDone_other: '{{count}} rows were left alone because they are approved.',
      matchedBy: {
        payrollId: 'by payroll number',
        name: 'by name',
        manual: 'assigned by hand',
      },
      assign: 'Assign to',
      discard: 'Discard',
      unassigned: 'Nobody — will not be saved',
      source: 'List {{symbol}} for {{from}} – {{to}}',
      sourceMismatch: 'This file is for a different month than the one on screen.',
      payerName: 'Optima payer: {{name}}',
      company: 'Company',
      companyPick: 'Choose a company…',
      companyNeeded: 'Link this Optima list to a company',
      companyNeededHint:
        'The payer name is not linked yet. Pick a company once — it is saved on that company in Admin → Companies, and the next import of this payer will match on its own.',
      companyLinked: 'Company: {{name}}',
      companyRequired: 'Pick a company before confirming the import.',
      form: 'Contract in this file',
      formDerived: 'Taken from the file as {{form}}. Change it if that is wrong.',
      formUnknown: 'The file did not say which contract this is. Pick one.',
      overwrites: 'replaces an existing row',
      unmatched_one: 'Unmatched: {{count}} row',
      unmatched_other: 'Unmatched: {{count}} rows',
      unmatchedNote: 'Assign each row to a person, or discard it. Discarded rows are not written.',
      unmatchedEmpty: 'Every row in the file has an employee.',
      unnamedRow: 'Row with no name and no number',
      reason: {
        'no-match': 'no such employee was found',
        'ambiguous-name': 'two people share this name — fill in a payroll number',
        'no-identifier': 'the row gives neither a payroll number nor a name',
        // Never a guess about which contract the figures belong to: the fix is the
        // file's "Umowa" column.
        'form-mismatch': 'this person holds no such agreement this month — fill in the "Umowa" column',
      },
      confirm_one: 'Write {{count}} row',
      confirm_other: 'Write {{count}} rows',
      applying: 'Writing…',
      done_one: 'Wrote {{count}} row, {{overwritten}} of them replacing an existing one.',
      done_other: 'Wrote {{count}} rows, {{overwritten}} of them replacing an existing one.',
      cancel: 'Cancel',
      close: 'Close',
    },
    close: {
      open: 'Close month',
      title: 'Close {{month}}',
      what: 'Closing stops every change to this month — work days, absences and corrections as well as payroll.',
      readiness: 'Checked rows: {{ready}} of {{active}}.',
      allReady: 'Every employee under an agreement this month has a checked row.',
      blocking_one: '{{count}} person is holding the close up',
      blocking_other: '{{count}} people are holding the close up',
      blockingNote: 'A count alone would not say who to look for, so the list gives names.',
      notTicked: 'row not checked',
      noEntry: 'no row at all',
      confirm_one: 'Close month — {{count}} row',
      confirm_other: 'Close month — {{count}} rows',
      closing: 'Closing…',
      done: 'The month is closed.',
      cancel: 'Cancel',
    },
    reopen: {
      open: 'Reopen',
      title: 'Reopen {{month}}',
      what: 'Reopening undoes the close and makes this month’s figures and records editable again.',
      reason: 'Reason for reopening',
      reasonHint_one: 'At least {{count}} character. The reason goes into the audit trail and a person will read it.',
      reasonHint_other: 'At least {{count}} characters. The reason goes into the audit trail and a person will read it.',
      confirm: 'Reopen the month',
      reopening: 'Reopening…',
      done: 'The month is open again.',
      cancel: 'Cancel',
    },
    restore: {
      open: 'Restore month',
      title: 'Restore {{month}}',
      what: 'Deletes the unapproved payroll rows for this month and recreates empty rows from today’s employees and agreements. Approved rows are left alone. This cannot be undone.',
      closed: 'This month is closed — restore does not run. Reopen the month first.',
      entryCount: 'Rows that will be deleted',
      entryNote: 'Imports and hand edits on unapproved rows all go',
      keptCount: 'Approved rows that stay',
      keptNote: 'Left exactly as they are, figures and all',
      expectedCount: 'Rows that will be recreated',
      expectedNote: 'Active employees with an agreement in force this month',
      warning_one: 'Restoring deletes {{count}} row and all of its figures. This cannot be undone.',
      warning_other: 'Restoring deletes {{count}} rows and all of their figures. This cannot be undone.',
      confirm_one: 'Delete {{count}} row and restore',
      confirm_other: 'Delete {{count}} rows and restore',
      running: 'Restoring…',
      done: 'Deleted {{deleted}} rows and recreated {{created}}.',
      doneKept_one: '{{count}} approved row was kept.',
      doneKept_other: '{{count}} approved rows were kept.',
      cancel: 'Cancel',
      close: 'Close',
    },
  },
  recalculate: {
    open: 'Rebuild month',
    title: 'Rebuild {{month}}',
    what: 'Rebuilds work days from the card reads. This cannot be undone.',
    scoped: 'Only the employees matching the filters on the timesheet.',
    storedDays: 'Days stored in this month',
    autoDays: 'Days derived from the reader',
    autoNote: 'These may be removed or changed',
    manualDays: 'Days entered by hand',
    manualNote: 'These are left alone',
    dayOffDays: 'Absences',
    workLogs: 'Card reads',
    employees: 'Employees',
    warning_one: 'Rebuilding may remove or change {{count}} reader-derived day. This cannot be undone.',
    warning_other: 'Rebuilding may remove or change {{count}} reader-derived days. This cannot be undone.',
    confirm_one: 'Rebuild {{count}} day',
    confirm_other: 'Rebuild {{count}} days',
    running: 'Rebuilding…',
    done_one: 'Rebuilt {{count}} day.',
    done_other: 'Rebuilt {{count}} days.',
    cancel: 'Cancel',
    close: 'Close',
  },
  warning: {
    no_exit: 'No exit',
    is_working: 'At work',
    long_hours: 'Long day',
    short_hours: 'Short day',
    conflict: 'Absence conflict',
    overnight: 'Overnight shift',
  },
  form: {
    required: 'This field is required',
    choose: '— choose —',
  },
  reference: {
    id: 'ID',
    name: 'Name',
    usage: 'In use',
    unused: 'unused',
    availableIn: 'Available in',
    availableInHint: 'Tick the departments this position can be used in.',
    availableEverywhere: 'Available in every department.',
    everywhere: 'everywhere',
    usageCount_one: '{{count}} agreement',
    usageCount_other: '{{count}} agreements',
    empty: 'Nothing here.',
    searchPlaceholder: 'Part of a name',
    editTitle: 'Rename {{name}}',
    confirmDelete: 'Delete “{{name}}”?',
    companiesMoved: 'Companies are in the Admin module.',
    companiesLink: 'Go to companies',
    kind: {
      departments: 'Departments',
      positions: 'Positions',
    },
    add: {
      departments: 'Add a department',
      positions: 'Add a position',
    },
  },
  employees: {
    id: 'ID',
    name: 'Employee',
    firstName: 'First name',
    lastName: 'Last name',
    payrollId: 'HR number',
    payrollIdHint: 'Optima akronim — e.g. 0075/E. Filled automatically on the first import that matches this person.',
    activate: 'Reactivate',
    activated:
      '{{name}} is active again. The agreement and the card are not restored — add a new agreement for them to appear in the timesheet and on payroll.',
    rented: 'Rented employee',
    rentedHint:
      'Another company reimburses this person\u2019s whole cost plus a margin. The reimbursement appears as a negative figure in the Invoices column on payroll. Employment and mandate contracts only.',
    rentedMargin: 'Margin (%)',
    rentedMarginHint:
      'For example 10 means a cost of 8 000 PLN is reimbursed as 8 800 PLN. Changing the margin does not restate months already settled — each row keeps the margin it was created with.',
    disabled: 'Disabled employee',
    disabledHint:
      'A seven-hour working day: the monthly norm is 7/8 of the standard one and each day of leave counts 7 h. Applies from the date below onward — earlier months keep the figures they already have.',
    disabledFrom: 'From',
    disabledFromHint: 'First month the seven-hour day applies.',
    disabledTo: 'Until',
    disabledToHint: 'Leave empty while the status is current.',
    countsHours: 'Hours are recorded',
    countsHoursHint:
      'Off for somebody with no card. Nothing scans them, so payroll hours are filled from their contracted norm, less any leave — until an HR import states a real figure.',
    department: 'Department',
    position: 'Position',
    card: 'Card',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    allStatuses: 'Everyone',
    noAgreement: 'no current agreement',
    departmentBadge: 'Department: {{departments}}',
    departmentsBadge: 'Departments: {{departments}}',
    noCard: 'no card',
    empty: 'No employees found.',
    searchPlaceholder: 'First or last name',
    add: 'Add an employee',
    addHint: 'The first agreement is required. A card is added on the person’s page.',
    editTitle: 'Edit employee',
    backToList: 'Back to the list',
    cardHint: 'The proximity card number the readers see.',
    cardNumber: 'Printed number',
    cardNumberHint: 'Ten digits, as printed on the card.',
    cardHex: 'Reader number',
    issueCard: 'Assign a card',
    issueCardHint: 'Type the ten-digit number printed on the card. Nothing is written to a reader.',
    saveCard: 'Save the card',
    replaceCard: 'Replace the card',
    removeCard: 'Remove the card',
    confirmRemoveCard: 'Take the card away?',
    pickerHint: 'Type at least two letters.',
    month: {
      title: 'Working time',
      description:
        'This employee’s days in the selected month. Click a day to correct it, or an empty day to record one.',
      empty: 'No records in this month.',
      logUnused: '{{type}} {{time}} — not used for the work day',
      logNextDay: '{{type}} {{time}} the following morning — a night shift',
      logCorrected: '{{type}} {{time}} — the corrected time this day is billed at',
      // A superscript on a time, so it has to stay a couple of characters.
      nextDayMark: '+1',
    },
    addWorkDay: 'Add a day',
    // No longer "a manual entry": the dialog books an absence too, and an
    // absence is not a manual work day.
    addWorkDayHint: 'A hand-entered day for {{name}}. Rebuilding the month will leave it alone.',
    workDayDate: 'Date',
    workDayEndHint: 'Empty means no exit was recorded. An earlier time than the entry means a night shift.',
  },
  agreements: {
    title: 'Agreements',
    change: 'Change agreement',
    addSecond: 'Second agreement',
    addSecondTitle: 'Add a second agreement',
    addSecondHint:
      'A new agreement alongside the current one, which is left running. Allowed only at a different company. The hours stay with the existing agreement.',
    changeTitle: 'Change agreement',
    changeHint:
      'The current agreement ends the day before this date. A new one starts from it. Use Correct to fix a row without starting a new period.',
    changeDateHint:
      'Not before the current agreement starts. That agreement ends the day before.',
    correct: 'Correct',
    editTitle: 'Correct agreement',
    hint: 'An agreement decides the rate and which departments the timesheet counts this person under.',
    period: 'Period',
    openEnded: 'open ended',
    rate: 'Rate',
    rateHint: 'Gross for UoP and Zlecenie; the invoice value for B2B.',
    etat: 'Etat',
    etatHint:
      'Part of a full-time post. Scales the monthly hours norm only — enter the actual salary in Rate, not a full-time one.',
    isPrimary: 'Hours',
    isPrimaryHint:
      'The agreement worked hours are booked against. An employee may hold two at once when the companies differ — the hours belong to only one of them.',
    isPrimaryYes: 'Booked against this agreement',
    isPrimaryNo: 'Booked against another agreement',
    perHour: 'PLN/h',
    perMonth: 'PLN/mo',
    dateFrom: 'From',
    dateTo: 'To',
    dateToHint: 'Empty means an open-ended agreement.',
    departments: 'Departments',
    departmentsHint:
      'Cost divides between the departments in proportion to their weights. Two weights of 1 is an even split; 0.7 and 0.3 is a 70/30 split.',
    addDepartment: 'Add a department',
    positionNotInDepartments:
      '{{position}} is not normally used in {{departments}}. You can still save this.',
    positionElsewhere: '{{name}} (another department)',
    weight: 'Weight',
    formLabel: 'Contract form',
    formHint: 'Decides the employer contribution on top of the gross. B2B carries none. Inne cannot hold a salary.',
    form: {
      uop: 'UoP',
      zlecenie: 'Zlecenie',
      b2b: 'B2B',
      inne: 'Other',
    },
    rateUnitLabel: 'Rate period',
    rateUnitHint: 'Whether the rate above is per hour or per month.',
    empty: 'No agreements.',
    confirmDelete: 'Delete this agreement?',
    payBSchemaLabel: 'Pay B type',
    payBValueLabel: 'Value',
    payBSchemaHint: 'Choose a Pay B type or leave empty.',
    payBValueHint: 'Pay B amount in PLN.',
    payBSchema: {
      none: '— none —',
      fixed_compensation_month: 'Fixed monthly compensation',
      fixed_bonus_month: 'Fixed monthly bonus',
      fixed_compensation_hour: 'Fixed hourly compensation',
    },
    payBSchemaHelp: {
      fixed_compensation_month: 'Fixed compensation calculated monthly.',
      fixed_bonus_month: 'Fixed bonus paid each month.',
      fixed_compensation_hour: 'Fixed compensation calculated per hour worked.',
    },
  },
  daysOff: {
    add: 'Add an absence',
    editTitle: 'Edit absence',
    hint: 'An absence covers a range of days and counts as 8 hours per day.',
    period: 'Period',
    days: 'Days',
    dayCount_one: '{{count}} day',
    dayCount_other: '{{count}} days',
    type: 'Kind',
    month: 'Month',
    allMonths: 'All',
    from: 'From',
    to: 'To',
    toHint: 'This day is off as well.',
    empty: 'No absences.',
    confirmDelete: 'Delete the absence for {{name}}?',
  },
  cardReaders: {
    add: 'Add a reader',
    editTitle: 'Edit reader',
    id: 'ID',
    location: 'Location',
    address: 'Address',
    ip: 'IP address',
    port: 'Port',
    active: 'Active',
    activeHint: 'Inactive readers are not polled.',
    inactive: 'Inactive',
    allStates: 'All',
    connected: 'Connected',
    disconnected: 'Not answering',
    never: 'never',
    lastSynced: 'Last synchronised',
    lastLog: 'Newest reading',
    state: 'State',
    empty: 'No readers.',
    searchPlaceholder: 'Part of a location',
    synchronize: 'Synchronise',
    synchronizeTitle: 'Synchronise {{name}}',
    synchronizeHint:
      'Fetches missing readings from the device. Empty dates mean everything since the last full synchronisation.',
    synchronizing: 'Synchronising…',
    synchronizingHint: 'Connecting to {{address}}. This may take a while.',
    synchronizeDone_one: 'Stored {{count}} reading.',
    synchronizeDone_other: 'Stored {{count}} readings.',
    from: 'From',
    fromHint: 'Empty: since the last full synchronisation.',
    to: 'To',
    toHint: 'Empty: up to now.',
    badRange: 'That date range is not valid.',
    confirmDelete: 'Delete the reader “{{name}}”?',
    username: 'User name',
    usernameHint: 'The device account. “admin” unless somebody changed it.',
    password: 'Password',
    passwordStored: 'A password is stored for this reader (set {{date}}). Type a new one to replace it.',
    passwordShared: 'No password of its own — this reader uses the shared one from the server configuration.',
    passwordClear: 'Use the shared password',
    passwordClearHint: 'Removes this reader’s own password and falls back to the server configuration.',
    // Ingest — polled by the server, or sending its own readings.
    ingest: 'Ingest',
    ingestTitle: 'Ingest: {{name}}',
    ingestHint:
      'A reader can be polled by the server, or send every reading itself. Migrate one device at a time.',
    mode: 'Mode',
    modePull: 'Polled',
    modePullHint:
      'The server downloads the whole log file every 10 minutes. This is what every reader does today.',
    modeShadow: 'Shadow',
    modeShadowHint:
      'The reader sends its readings and we keep them only for comparison — polling is still what counts. A transitional state, for a day.',
    modePush: 'Sending',
    modePushHint:
      'The reader’s own readings are the record; the scheduled poll skips it. A full catch-up runs once a night, and synchronising by hand still works.',
    modeCollector: 'Collector',
    modeCollectorHint:
      'A collector on the site’s network reads the reader and sends its readings under its data source. The server never connects to the reader.',
    source: 'Data source',
    sourceHint: 'A “Card readers” source whose token the collector holds. Sources are added on Administration → Data sources.',
    sourcePick: 'Choose a source',
    sourceNone: 'No “Card readers” sources. Add one on Administration → Data sources.',
    sourceOff: '{{name}} (off)',
    collectorReads: 'collector: {{name}}',
    collectorId: "In collector.toml, in this reader's [[reader]] block:",
    collectorLastPush: 'Collector’s last push',
    collectorNoConnect: 'A collector reads this reader. The server does not connect to it.',
    ingestUrl: 'The address to type into the reader',
    urlHint: 'On the device: Network → HTTP Client → Resource. Then enable HTTP Client.',
    urlNoBase:
      'The server does not know its own public address (READER_PUSH_BASE_URL), so only the path is shown.',
    urlAfterMode: 'The address appears once shadow or sending mode is saved.',
    copy: 'Copy',
    copied: 'Copied',
    copyUrl: 'Copy the address',
    lastPush: 'Last received',
    quiet:
      'Nothing has arrived from this reader for over {{minutes}} minutes. Check that HTTP Client is enabled and the address is right.',
    quietShort: 'silent',
    regenerate: 'New token',
    regenerateConfirm:
      'Issue a new token for “{{name}}”? The current address stops working until you type the new one into the reader.',
    regenerateHint: 'The old address stops working at once — the reader goes silent until the new one is typed in.',
    test: 'Test',
    testTitle: 'Connection test: {{name}}',
    testHint: 'Reads {{address}} and reports what it found. Nothing is stored.',
    testing: 'Contacting the reader…',
    testAgain: 'Test again',
    testOk: 'The reader answered in {{ms}} ms.',
    testFailure: {
      unreachable: 'No answer from this address.',
      unauthorized: 'The reader refused these credentials.',
      'not-a-reader': 'Something answered, but it is not a card reader.',
      error: 'The test could not be completed.',
    },
    testUser: 'Signed in as',
    testSource: {
      device: 'this reader’s own password',
      environment: 'the shared password',
    },
    testMode: 'Mode',
    testReadOnly: 'Read-only — this server never writes to a reader.',
    testDevice: 'Device',
    testModel: 'Model',
    testFirmware: 'firmware {{version}}',
    testClock: 'Device clock',
    testClockUnknown: 'not reported by this firmware',
    testSkew: '{{seconds}} s from this server',
    testStoredLogs: 'Readings held',
    testCardsOnDevice: 'Cards known to the device',
    testLog: 'Log file',
    testLogRows: 'Rows',
    testLogWindow: 'Covering',
    testWouldStore: 'New readings',
    testWouldStoreValue_one: '{{count}} would be stored',
    testWouldStoreValue_other: '{{count}} would be stored',
    testMatched: 'Recognised cards',
    testMatchedValue_one: '{{count}} belongs to an employee',
    testMatchedValue_other: '{{count}} belong to employees',
    testUnmatched: 'Not held by anyone: {{cards}}',
    testRaw: 'Show the raw log lines',
    testRawStatus: 'Show what the device reported',
    testLogMissing: 'The log file could not be read.',
  },
  history: {
    title: 'Change history',
    description: 'Who changed this person’s details or agreements, and when.',
    empty: 'Nothing recorded.',
    hidden: 'change hidden',
    action: {
      create: 'Created',
      update: 'Changed',
      delete: 'Deleted',
    },
    entity: {
      'work-day': 'work day',
      'work-log': 'card read',
      employee: 'employee details',
      agreement: 'agreement',
      device: 'reader',
      'position-departments': 'position departments',
    },
    // History labels for fields the form names only in context ("From" under the
    // disability, "Value" under pay B).
    field: {
      disabledFrom: 'Disability from',
      disabledTo: 'Disability to',
      payBValue: 'Pay B amount',
    },
  },
  workLogs: {
    subtitle: 'Raw card reads — for auditing a reader, or one person\u2019s day.',
    time: 'Date and time',
    clock: 'Time',
    clockHint: 'HH:MM. The date of the read does not change.',
    direction: 'Direction',
    entry: 'Entry',
    exit: 'Exit',
    reader: 'Reader',
    handEntered: 'Hand-entered',
    workDay: 'Work day',
    noWorkDay: 'none',
    noWorkDayHint:
      'This read neither opens nor closes a work day — either the day was built from other reads, or no day was built at all.',
    openDay: 'Open the day',
    empty: 'No reads match these filters.',
    from: 'From',
    to: 'To',
    deleted: 'Deleted',
    deletedHidden: 'Hide deleted',
    deletedShown: 'Show deleted',
    editTitle: 'Edit a read',
    editHint: '{{date}}, {{name}}. Changing the time can move the work day.',
    badTime: 'The time must be HH:MM.',
    confirmDelete: 'Delete {{name}}\u2019s read at {{time}}? The work day may be rebuilt.',
    recalculate: 'Re-derive entry/exit',
    recalculateTitle: 'Re-derive entry and exit',
    recalculateWhat:
      'Decides each read\u2019s direction again: a read is an exit if it follows an entry within 14 hours. This changes what a month rebuild would then derive.',
    recalculateScoped: 'For the selected employee only.',
    rangeBackwards: '“From” must not be later than “to”.',
    previewLogs: 'Reads in range',
    previewLogsNote: 'Any of these may change direction',
    previewEmployees: 'Employees',
    previewWorkDays: 'Work days in range',
    previewWorkDaysNote: 'Unchanged by this — only by a month rebuild',
    recalculateConfirm_one: 'Recalculate {{count}} read',
    recalculateConfirm_other: 'Recalculate {{count}} reads',
    recalculateRunning: 'Recalculating…',
    recalculateDone_one: '{{count}} read changed direction.',
    recalculateDone_other: '{{count}} reads changed direction.',
  },
  account: {
    title: 'Account',
    passwordTitle: 'Password',
    passwordHint: 'The password lives in Cognito, not in this application.',
    currentPassword: 'Current password',
    changePassword: 'Change password',
    changing: 'Changing…',
    passwordChanged: 'The password has been changed.',
    wrongPassword: 'The current password is not right.',
    appearance: 'Appearance',
    appearanceHint: 'Saved to your account — they apply on every device you sign in on.',
    locale: {
      pl: 'Polski',
      en: 'English',
    },
    themeSystemNote: 'The system theme keeps following the operating system.',
    savedFilters: 'Saved filters',
    savedFiltersHint:
      'Timesheet, employees, absences, card reads, payroll and card readers remember your last filters and sorting — on your account, so on every device. A link with filters in its address always wins.',
    forgetFilters: 'Forget saved filters',
    forgetFiltersQuestion: 'Remove the saved filters and sorting from every screen?',
    forgetFiltersConfirm: 'Remove',
    filtersForgotten: 'Saved filters removed. Lists will open with their defaults.',
    access: 'Access',
    accessHint: 'As the server sees it — from GET /v1/me, not from a token decoded in the browser.',
    groups: 'Groups',
    noGroups: 'none',
    departments: 'Departments',
    noDepartments: 'none',
    allDepartments: 'all',
    canSeeCosts: 'May see costs',
    readOnly: 'Access',
    readOnlyHint: 'Read-only — no changes possible',
  },
  modules: {
    switch: 'Switch application',
    pickPrompt: 'Choose an application',
    pickRemembered: 'We will remember your choice on your account. Change it by clicking the logo.',
    noneTitle: 'You have no application',
    noneBody:
      'Your account works, but no application has been assigned to it yet. Ask an administrator for access.',
    hr: {
      name: 'HR',
      description: 'Timesheets, employees, payroll',
    },
    hotel: {
      name: 'Hotel',
      description: 'Occupancy, ADR, RevPAR and the book',
    },
    gastronomy: {
      name: 'Gastronomy',
      description: 'Restaurant, cafe, grill, pizzeria, events, horse riding',
    },
    catering: {
      name: 'Catering',
      description: 'Staff canteens: meals, the employee card and receipts',
    },
    cashier: {
      name: 'Cashier',
      description: 'Day report, report history and the main desk',
    },
    admin: {
      name: 'Admin',
      description: 'Users, their access, and companies',
    },
  },
  hotel: {
    nav: {
      today: 'Today',
      results: 'Results',
      forward: 'Forward',
      settings: 'Settings',
    },
    filter: {
      label: 'Object',
      allObjects: 'All objects',
    },
    money: {
      label: 'Net or gross',
      nett: 'Net',
      gross: 'Gross',
      vatNote: 'You are entering {{mode}}. {{otherMode}}: {{amount}} (VAT 8 %).',
    },
    metrics: {
      occupancy: 'Occupancy',
      adr: 'ADR',
      revpar: 'RevPAR',
      trevpor: 'TRevPOR',
      trevpar: 'TRevPAR',
      alos: 'ALOS',
      // Each one names its denominator, because that is the only difference
      // between the four — and it is what the acronym hides.
      explain: {
        adr: 'Room revenue ÷ rooms sold. The average price of a room that sold.',
        trevpor:
          'Total revenue (rooms + F&B) ÷ rooms sold. What one sold room leaves behind.',
        revpar:
          'Room revenue ÷ rooms available. Falls when the price is low OR when rooms sit empty.',
        trevpar:
          'Total revenue (rooms + F&B) ÷ rooms available. The widest measure — empty rooms count too.',
        alos: 'Sold nights ÷ number of stays. The average length of one stay, in nights.',
      },
      yoy: 'Year ago: {{value}}',
      yoyRevenue: 'Revenue year ago: {{value}}',
    },
    grid: {
      sold: 'Sold',
      free: 'Free',
      shortBlock: 'Short block',
      softBlock: 'Soft block',
      longBlock: 'Long block',
      rent: 'Rent',
      manual: 'Manual override',
      ungrouped: 'No object',
      editNight: 'Set price for room {{room}}',
      editManual: 'Edit manual price for room {{room}}',
      editTitle: 'Room {{room}}',
      editHint: 'Change only this night ({{date}}).',
      editRateHint: 'Leave empty for an event — no amount in ADR.',
      status: {
        sold: 'Sold',
        free: 'Free',
        blocked: 'Blocked',
        absent: 'Absent',
      },
    },
    qualify: {
      gratis: 'comp / own use',
      impreza: 'event invoice',
      noc: 'room rate',
    },
    today: {
      title: 'Today',
      subtitle: 'Room grid and morning metrics',
      unpricedPill: '{{count}} unpriced',
      dayRevenue: 'Day revenue',
      cash: 'Cash',
      cashNote: 'Cash by document date; open stays may differ.',
      cashAllGroupsNote: 'For all objects, not only the selected one — cash is not split by group.',
      arrivals: 'Arrivals',
      departures: 'Departures',
      roomsPersons: 'rooms / persons',
      breakfasts: 'Breakfasts',
      dinners: 'Half-board dinners',
      breakfastsPayable_one: '{{count}} payable tomorrow morning',
      breakfastsPayable_other: '{{count}} payable tomorrow morning',
      mealsAdultChild: 'adults / children',
      sellableDenom: 'of {{count}} sellable',
      bridgeTitle: 'RevPAR bridge',
      bridgeHint: 'Year-over-year change: rate vs occupancy',
      bridgeStart: 'Last year',
      bridgeEnd: 'This day',
      bridgeOcc: 'Occupancy effect',
      bridgeAdr: 'Rate effect (ADR)',
      bandTitle: 'Same weekdays',
      bandHint: 'RevPAR on the last eight matching weekdays',
      bandMedian: 'median {{value}}',
      bandPrior: '8 prior',
      bandSelected: 'selected day',
      revenueSplit: 'Revenue split',
      revenueSplitTotal: 'Total',
      source: 'Source',
      value: 'Value',
      share: 'Share',
      perSold: 'Per sold night',
      rev: {
        rooms: 'Rooms',
        breakfast: 'Breakfast',
        dinner: 'Dinner',
        gastro: 'Restaurant',
        bar: 'Bar',
        other: 'Other',
      },
      historyTitle: 'History',
      historyHint: 'Compare up to three days',
      historySelected: 'Selected',
      historyYoy: 'Year ago',
      historyYoy2: 'Two years ago',
      historyCalendar: 'Calendar year ago',
      colDay: 'Day',
      colOcc: 'Occ.',
      colRevenueTotal: 'Total revenue',
      chartOccupancy: 'Occupancy',
      chartRevpar: 'RevPAR',
      openFolioNote: '{{amount}} on open folios — not in day revenue yet',
      grid: 'Room grid',
      emptyGrid: 'No room data for this day.',
      priceGaps: 'Unpriced nights — manual fill',
      priceGapsHint:
        'The grid says the room was sold, but there is no amount. Some will later come from Events — for now it is a manual decision.',
      priceGapsBulk: 'Apply to all {{count}} nights on this day:',
      priceGapsRatePh: 'PLN / night',
      priceGapsApply: 'Apply',
      priceGapsSave: 'Save',
      priceGapsNeedRate: 'Room rate needs an amount.',
      priceGapsDirty_one: '{{count}} unsaved change',
      priceGapsDirty_other: '{{count}} unsaved changes',
      priceGapsColRoom: 'Room',
      priceGapsColPax: 'Pax',
      priceGapsColStay: 'Stay',
      priceGapsColRes: 'Reservation',
      priceGapsColKind: 'Qualification',
      priceGapsColRate: 'Rate',
      priceGapsColApply: 'Apply to…',
      priceGapsStayNights_one: '{{count}} night',
      priceGapsStayNights_other: '{{count}} nights',
      priceGapsResSummary: '{{nights}} nights in {{rooms}} rooms',
      priceGapsResOpen_one: '{{count}} unpriced',
      priceGapsResOpen_other: '{{count}} unpriced',
      priceGapsNoRes: 'no reservation',
      priceGapsApplyStay: 'stay ({{count}})',
      priceGapsApplyRes: 'reservation ({{count}})',
      priceGapsSuggestNote:
        'The rate hint is the median for this room in the month — a starting point, not a blind approval.',
      priceGapsEventNote:
        'One reservation covers {{count}} unpriced rooms. That looks like an event billed separately. Use “reservation” to set all its nights at once.',
    },
    results: {
      title: 'Results',
      subtitle: 'Metrics for the selected period',
      attributed: 'Revenue always attributed',
      periodRevenue: 'Period revenue',
      nightsUnit: 'nights',
      breakfastCoverage: 'Breakfasts',
      dinnerCoverage: 'Half-boards',
      mealCoverageHint: 'guest coverage',
      unpricedQueue: 'Unpriced nights',
      unpricedQueueHint: 'Days that still have sold nights without a price',
      unpricedCount_one: '{{count}} unpriced night',
      unpricedCount_other: '{{count}} unpriced nights',
      bridgeEnd: 'This period',
      weekdayEyebrow: 'Weekly rhythm',
      weekdayTitle: 'Occupancy by day of week',
      weekdayHint: 'Mean occupancy in the selected period',
      weekdayEmpty: 'No days with occupancy in this period.',
      weekdayDays_one: '{{count}} day',
      weekdayDays_other: '{{count}} days',
      occRevparTitle: 'Occupancy and RevPAR',
      occRevparHint: 'Daily occupancy (bars) and RevPAR (line) for the period',
      sourcesEyebrow: 'Reservation source',
      sourcesTitle: 'Reservations by source field',
      sourcesHint: 'Stay starts in the period, excluding cancellations',
      sourcesBlank: '(not saved)',
      sourcesEmpty: 'No source data in this period yet.',
      sourcesNote:
        'The darker segment is reservations with a Profitroom id — they arrived online regardless of what reception typed.',
      sourcesTip: '{{count}} reservations · {{profitroom}} with Profitroom id',
    },
    forward: {
      title: 'Forward look',
      asOf: 'Book as of {{date}}',
      otbOcc: 'OTB occupancy',
      books: 'Nights on the books',
      ofSellable: 'of {{count}} sellable',
      gapToFinal: '{{count}} short of prior-year final',
      pickup1: 'Pickup 1 day',
      pickup3: 'Pickup 3 days',
      pickup7: 'Pickup 7 days',
      nights: 'nights',
      cancellations: 'Cancellations',
      cancellationsHint: 'of reservations historically',
      priorFinal: 'prior year final {{value}}',
      horizon: 'Horizon',
      horizonDays_one: '{{count}} day',
      horizonDays_other: '{{count}} days',
      calendarTitle: 'Reservation calendar',
      calendarHint: 'booked occupancy for the next {{count}} days, on sellable capacity',
      calendarEmpty: 'No book in this horizon.',
      calendarCell: '{{date}}: {{occ}} %, in {{ahead}} days',
      occRevparTitle: 'RevPAR forecast',
      occRevparHint: 'On-the-books occupancy (%) and provisional RevPAR for {{count}} days',
      attention: 'Days needing attention',
      attentionHint: 'Fewer bookings than a year ago at the same lead',
      attentionEmpty: 'No dates materially off pace in this horizon.',
      attentionNote: 'Working list: a discount, package or campaign belongs here.',
      attentionAhead_one: 'in {{count}} day',
      attentionAhead_other: 'in {{count}} days',
      attentionCompare: 'now {{now}} · year ago {{prior}}',
      attentionRooms_one: '{{count}} rms',
      attentionRooms_other: '{{count}} rms',
      leadTimeTitle: 'Booking lead time',
      leadTimeHint: 'How many days before arrival guests book',
      leadTimeNote:
        'Price steering matters about three weeks out — and OTB by definition will miss one fifth of guests.',
      leadTimeEmpty: 'No lead-time data.',
      leadTimeCount: 'Bookings',
      leadTimeBucket: '{{range}} days before arrival',
    },
    settings: {
      title: 'Settings',
      readOnly: 'You do not have permission to change hotel settings.',
      previewFailed: 'Could not calculate the preview.',
      subtitle: 'Long-term rent, blocks and meal rates',
      draftPending: 'You have unsaved changes. Save or undo.',
      save: 'Save',
      undo: 'Undo',
      draft: 'Draft',
      addDraft: 'Add to draft',
      roomGroups: 'Room groups',
      roomGroupsHint: 'Objects for filtering — name and sort order',
      rooms: 'Rooms',
      roomsHint: 'Room configuration — name, group and Izzy identifier',
      groupName: 'Group name',
      sortOrder: 'Sort order',
      roomName: 'Room name',
      izzyId: 'Izzy identifier',
      validFrom: 'Valid from',
      validTo: 'Valid to',
      alwaysValid: 'Always',
      orphanRoom: 'No group',
      pickRoom: '— pick a room —',
      rents: 'Rent',
      rentsHint: 'Room stays in occupancy / RevPAR — priced from the lease (month ÷ days)',
      blocks: 'Long blocks',
      blocksHint:
        'Removes the room from sellable inventory. A soft block yields to a sale — the room returns to every metric on nights it was sold.',
      blockKindLabel: 'Kind',
      blockKind_long: 'Hard',
      blockKind_soft: 'Soft',
      meals: 'Meal rates',
      mealsHint: 'Effective from a date — do not rewrite history',
      room: 'Room',
      from: 'From',
      to: 'To',
      rate: 'Rate',
      unit: 'Unit',
      unitMonth: 'month',
      unitNight: 'night',
      reason: 'Reason',
      breakfast: 'Breakfast',
      dinner: 'Dinner',
      childFactor: 'Child factor',
      previewTitle: 'Draft preview',
      previewHint: 'Last 30 days with draft settings',
      previewOcc: 'Occ.',
      previewRevpar: 'RevPAR',
      previewSellable: 'Sellable',
    },
  },
  gastronomy: {
    events: {
      revenueShort: 'Revenue',
      salesHint: 'The events’ whole value: invoices and bills before the advances are taken off.',
      advancesHint: 'Advances taken in this time, before the event.',
      revenueHint: 'Revenue = sales + advances taken − advances settled on final invoices.',
      aheadHint_one: 'Issued {{count}} day before the event — an advance, or an invoice ahead of it.',
      aheadHint_other: 'Issued {{count}} days before the event — an advance, or an invoice ahead of it.',
      sales: 'Sales',
      advances: 'Advances',
      streamsTitle: 'Sales by stream',
      streamsHint: 'Invoices and bills before advances: the events’ whole value. The stream is read from each line’s text.',
      plusTaken: '+ advances taken',
      minusSettled: '− advances settled',
      revenue: '= revenue',
      documentsTitle: 'Documents ({{count}})',
      documentsHint: 'Each document with its lines — their text says what the event was. The event date from the text or from its advance.',
      noDocuments: 'No documents.',
      eventOn: 'event {{date}}',
      settles: 'settles: {{documents}}',
      settledBy: 'settled by: {{documents}}',
      kind: {
        gastronomy8: 'Catering services 8%',
        gastronomy23: 'Catering services 23%',
        services23: 'Services 23% (halls, activities)',
        hotel: 'Hotel services',
        advanceTaken: 'Advance taken',
        advanceSettled: 'Advance settled',
      },
    },
    payments: {
      title: 'Payments',
      hintDay: 'Share of revenue; change against the typical share — the median of {{weeks}} weeks.',
      hintPeriod: 'Share of revenue; change against the share in the same period a year earlier.',
      none: 'No payments on this day.',
      bills_one: '{{formatted}} bill',
      bills_other: '{{formatted}} bills',
      internal: 'Internal settlement: {{amount}} — not revenue',
      group: {
        employeeCard: 'Employee card',
        card: 'Card',
        cash: 'Cash',
        transfer: 'Bank transfer',
        online: 'Online (Profitroom, pyszne.pl)',
        prepaid: 'Prepayment and gift cards',
        room: 'Room charge (IzzyHotel)',
        other: 'Other',
        none: 'No payment',
      },
    },
    table: {
      showAll: 'Show all ({{total}})',
      showLess: 'Show fewer',
    },
    nav: {
      today: 'Today',
      results: 'Results',
      forward: 'Forward',
      controls: 'Controls',
      settings: 'Settings',
    },
    money: {
      label: 'Nett or gross',
      gross: 'Gross',
      nett: 'Nett',
    },
    outlets: {
      none: 'Unassigned',
      restaurant: 'Restaurant',
      cafe: 'Cafe',
      grill: 'Grill',
      pizzeria: 'Pizzeria',
      events: 'Events',
      riding: 'Riding / recreation',
    },
    eventKind: {
      wedding: 'Wedding',
      communion: 'Communion',
      birthday: 'Birthday',
      reception: 'Reception',
      meeting: 'Meeting',
      catering: 'Catering',
      stay: 'Stay',
      other: 'Other',
    },
    freshness: {
      final: 'Final figures',
      provisional: 'Provisional — may still change',
      lastPush: 'Last push {{at}}',
      never: 'The collector has not sent anything yet',
    },
    period: {
      reversed: 'The start date is after the end date.',
      tooLong: 'A period can be at most {{days}} days.',
    },
    chart: {
      empty: 'No revenue in this range.',
    },
    today: {
      title: 'Today',
      subtitle: 'One outlet\'s day against the same weekday in the 8 weeks before',
      subtitle_catering: 'One canteen\'s day against the same weekday in the 8 weeks before',
      empty: 'No bills or documents on this day.',
      outlets: 'Points of sale',
      outlets_catering: 'Canteens',
      revenue: 'Revenue',
      bills: 'Bills',
      noDetail: 'No assigned outlet had sales on this day.',
      noDetail_catering: 'No canteen had sales on this day.',
      yearAgo: 'a year ago',
      correctionHere: 'Corrected later: {{document}} ({{outlet}}), issued {{date}}, changes this day by {{amount}}.',
      correctionAway:
        'Correction {{document}} ({{outlet}}) issued this day counts on the day of the sale, {{date}}: {{amount}}.',
      correctionOutlet:
        'Correction {{document}}, entered in the series of {{series}}, counts for the outlet whose sale it corrects — {{outlet}}: {{amount}}.',
      correctionSeries: 'Entered in the series of {{series}}.',
      replacementHere:
        'Invoice issued again: {{document}} ({{outlet}}), {{date}}, in place of an invoice from this day — counted here: {{amount}}.',
      replacementAway:
        'Invoice {{document}} ({{outlet}}) issued again this day, in place of an invoice from {{date}}, counts on the day of the sale: {{amount}}.',
      goToDay: 'Show {{date}}',
      weatherTitle: 'Highest temperature; in brackets the lowest and highest ({{min}} – {{max}})',
      sunshine: 'Sun {{hours}} h',
      precipitation: 'rain {{mm}} mm',
      bandTitle: 'Revenue against 8 weeks',
      bandHint: 'The same weekday in the 8 weeks before. The median is of the days the outlet was open.',
      bandHint_catering: 'The same weekday in the 8 weeks before. The median is of the days the canteen was open.',
      openWeeks: 'Open days to compare with: {{weeks}} of 8',
      noWeeks: 'The outlet was not open on any of the 8 same weekdays before.',
      noWeeks_catering: 'The canteen was not open on any of the 8 same weekdays before.',
      median: 'Median',
      againstMedian: 'against the median',
      billsLine: 'Bills: {{bills}} · median {{median}}',
      hoursTitle: 'Revenue by hour',
      hoursHint: 'By the hour the bill closed. The line is that hour\'s median on the same weekday, over the {{weeks}} such days before with bills.',
      hoursNoWeeks: 'By the hour the bill closed. No weeks with bills to compare with.',
      hourLabel: '{{hour}}:00',
      thisDay: 'This day',
      menuTitle: 'Menu structure',
      menuHint:
        "Each category's share of bill sales; click a category for its Izzy groups, and a group for its products. The marker is its typical share: the median over {{weeks}} weeks.",
      menuGroup: 'Category',
      menuShare: 'Share',
      menuTypical: 'Typical',
      menuDiff: 'Difference',
      menuPoints: '{{value}} pp',
      menuNotToday: 'none today',
      menuNoCategory: 'No category',
      menuShowGroups: 'Izzy groups: {{count}}',
      menuProducts: 'products: {{count}}',
      menuView: 'View',
      menuViewBars: 'Bars',
      menuViewPie: 'Pie chart',
      menuPieHint: "Each category's share of bill sales",
      menuPieTypical: 'typically {{value}}',
      productsTitle: 'Best sellers',
      productsHint: "The day's quantity against the typical one: the median over {{weeks}} weeks.",
      change: 'Change',
      missingTitle: 'Usually sells, not today',
      missingHint: 'Sold in more than half the weeks, not once on this day.',
      missingLine: 'typically {{quantity}} · {{value}}',
      documentsOnly: 'The outlet had no bills on this day. Its revenue is from documents, so there are no hours, menu or best sellers.',
      documentsOnly_catering: 'The canteen had no bills on this day. Its revenue is from documents, so there are no hours, menu or best sellers.',
    },
    results: {
      title: 'Results',
      subtitle: "One outlet's period against the same period a year earlier (364 days, the same weekdays)",
      subtitle_catering: "One canteen's period against the same period a year earlier (364 days, the same weekdays)",
      empty: 'No revenue in this period.',
      noDetail: 'No assigned outlet had sales in this period.',
      noDetail_catering: 'No canteen had sales in this period.',
      dailyTitle: 'Revenue over time',
      dailyHint: 'Bars — this period; dashed line — the same weekdays a year earlier',
      grouping: 'Group by',
      byDay: 'Day',
      byWeek: 'Week',
      byMonth: 'Month',
      lastYear: 'A year earlier',
      menuHint:
        "Each category's share of bill sales; click a category for its Izzy groups, and a group for its products. The marker is its share in the same period a year earlier.",
      productsHint: 'The quantity in the period against the same period a year earlier.',
      thisPeriod: 'This period',
      typical: 'A year ago',
      missingTitle: 'Sold a year ago, not now',
      missingHint: 'Sold in the same period a year earlier, not once now.',
      missingLine: 'a year ago {{quantity}} · {{value}}',
    },
    otherRemarks: {
      title: 'Other, with a remark',
      hint: 'Lines of products split by remark whose remark matches no variant — "other" in the menu structure.',
      hintAssign:
        'Lines of products split by remark whose remark matches no variant — "other" in the menu structure. Pick a variant: the remark joins its words, and every such line, past ones included, counts there.',
      product: 'Product',
      remark: 'Remark',
      variant: 'Variant',
      assign: 'Put "{{text}}" in a variant',
      noVariants: 'no variants — add them in Settings',
      saving: 'Saving…',
    },
    menuMix: {
      noGroup: 'No group',
      empty: 'No sales in this period.',
    },
    items: {
      open: "The day's documents",
      title: "The day's documents — {{outlet}}",
      count: 'bills: {{bills}}, documents: {{documents}}',
      none: 'No bills or documents.',
      order: 'Order',
      byTime: 'By time',
      byAmount: 'By amount',
      prev: 'Previous',
      next: 'Next',
      page: 'Page {{page}} of {{pages}}',
      time: 'Time',
      document: 'Document / bill',
      payment: 'Payment',
      issued: 'On the document',
      revenue: 'Revenue, {{mode}}',
      bill: 'Bill {{number}}',
      table: 'table {{table}}',
      covers: 'guests: {{covers}}',
      movedFrom: 'from the till of: {{outlet}}',
      internal: 'of which internal settlement {{amount}} — not revenue',
      correction: 'correction',
      correctionMoved: 'correction issued {{date}}, counted on the day of the sale',
      replacement: 'invoice issued again',
      replacementMoved: 'invoice issued again {{date}}, counted on the day of the sale',
      noLines: 'No lines.',
    },
    heatmap: {
      title: 'Weekday × hour',
      hint: 'Bills by closing hour',
      metric: 'Measure',
      bills: 'Bills',
      revenue: 'Revenue',
      weekday: 'Weekday',
      empty: 'No bills in this period.',
      peak: 'Darkest cell: {{value}}',
    },
    products: {
      product: 'Product',
      quantity: 'Quantity',
      empty: 'No sales.',
      otherVariant: 'other',
    },
    forward: {
      title: 'Forward',
      subtitle: 'Table reservations and events ahead',
      takenOn: 'As of {{date}}',
      noSnapshot: 'No data — the collector has not sent reservations or events yet.',
      pickup: 'New reservations, {{days}} d',
      coversCount_one: '{{count}} guest',
      coversCount_other: '{{count}} guests',
      onBooks: 'Reservations ahead',
      cancelled: 'Cancelled',
      deposits: 'Event deposits',
      depositsHint: 'Deposits taken for future events',
      reservationsTitle: 'Covers by day',
      reservationsHint: 'Covers booked and cancelled, all points of sale together',
      reservationsEmpty: 'No reservations ahead.',
      daysTitle: 'Reservations day by day',
      daysHint: 'Cancelled: reservations / guests',
      date: 'Day',
      reservations: 'Reservations',
      coversBooked: 'Covers',
      coversCancelled: 'Cancelled covers',
      eventsTitle: 'Events by month',
      eventsHint:
        "From the deposits: the document's text gives the event's kind and date. Under the count — the deposits. KALH — bookings in the events calendar.",
      eventsEmpty: 'No deposits for future events.',
      month: 'Month',
      total: 'Total',
      bookings: 'KALH',
    },
    controls: {
      title: 'Controls',
      subtitle: 'What was changed at the till, and who allowed it',
      authorisedBy: 'Authorised by',
      outlet: 'Outlet',
      outlet_catering: 'Canteen',
      count: 'Count',
      lines: 'Lines',
      value: 'Value',
      reason: 'Reason',
      bills: 'Bills',
      noAuthoriser: '(not recorded)',
      noReason: '(no reason)',
      deletesTitle: 'Deleted lines',
      deletesHint: 'Deletes of sent lines that somebody authorised, by person and outlet',
      deletesHint_catering: 'Deletes of sent lines that somebody authorised, by person and canteen',
      deletesEmpty: 'No authorised deletes.',
      newLineDeletes: 'Deletes of lines not yet sent: {{value}}. Normal work — nothing to check.',
      priceChangesTitle: 'Price changes',
      priceChangesHint: 'Line price changes, by the person who authorised them',
      priceChangesEmpty: 'No price changes.',
      voidsTitle: 'Voids',
      voidsHint:
        'Cancelled lines that had been printed, at their price. Typos left out: a quantity above 20 or a price above 1000 zł.',
      voidsEmpty: 'No voids.',
      discountsTitle: 'Discounts',
      discountsHint: 'Bill discounts by reason; value taken off the list price',
      discountsEmpty: 'No discounts.',
      cancelledTitle: 'Cancelled bills',
      cancelledHint: 'By the reason the till recorded',
      cancelledEmpty: 'No cancelled bills.',
      correctionsTitle: 'Corrections counted with the sale',
      correctionsHint:
        'A correction changes the revenue of the day and outlet of the sale it corrects, not of the day it was issued or the series it was entered in. The books count it on the day it was issued.',
      correctionsHint_catering:
        'A correction changes the revenue of the day and canteen of the sale it corrects, not of the day it was issued. The books count it on the day it was issued.',
      countsFor: 'Counts for',
      replacement: 'issued again in place of the corrected one',
      enteredIn: 'entered in the series of: {{outlet}}',
      correctionsEmpty: 'No such corrections in this period.',
      correctionsDifference:
        'This period differs from the books by {{difference}}: {{into}} of corrections moved in from other periods, {{out}} moved out.',
      issuedOn: 'Issued',
      document: 'Document',
      saleDay: 'Counted on',
      otherMonth: 'other month',
      change: 'Change',
      internalTitle: 'Internal settlement (WEWN)',
      internalTitle_catering: 'Other internal settlement (WEWN)',
      internalHint:
        'Not revenue: bills settled internally, for example staff meals. Shown for audit.',
      internalHint_catering:
        'Not revenue: bills settled internally for a reason other than the employee card. The employee card is revenue, so it is not here.',
      internalEmpty: 'No internal settlement.',
      internalEmpty_catering: 'No other internal settlement.',
      unpaidTitle: 'Bills with no payment',
      unpaidHint:
        'Bills closed with no payment at all: a typo, a void or a meal not paid for. They are not revenue. Value as the till has it.',
      unpaidEmpty: 'No bills with no payment.',
      unpaidLargest: 'The largest. Click a number to see its lines.',
      date: 'Day',
      bill: 'Bill',
    },
    settings: {
      title: 'Settings',
      subtitle: 'Where revenue counts: outlets, points of sale, series and menu categories',
      readOnly: 'View only. A general manager or an administrator can change these.',
      duplicateName: 'This outlet already has a category with that name.',
      unplacedPointsOfSale: 'Points of sale with no outlet: {{names}}',
      unplacedSeries: 'Series with no outlet: {{names}}',
      unassignedGross: 'Revenue with no outlet in the last 365 days: {{gross}}',
      mappingTitle: 'Points of sale and series',
      mappingHint:
        'Which outlet the revenue of each Izzy point of sale and each document series counts for. A change saves at once.',
      pointsOfSale: 'Points of sale',
      series: 'Document series',
      posId: 'No.',
      seriesCode: 'Code',
      izzyName: 'Name in Izzy',
      outlet: 'Outlet',
      outletFor: 'Outlet for: {{name}}',
      noOutlet: '— none —',
      outOfGastronomy: '— not gastronomy —',
      canteen: 'Canteen — out of gastronomy on purpose',
      outletsTitle: 'Outlets',
      outletsHint: 'Name, kind and order of the outlets on every gastronomy screen.',
      outletName: 'Name',
      outletCode: 'Code',
      isFood: 'Food',
      isFoodFor: 'Food: {{name}}',
      recordsCovers: 'Records covers',
      recordsCoversFor: 'Records covers: {{name}}',
      outletInUse_one: '{{count}} point of sale, series or rule counts here — move it to another outlet first.',
      outletInUse_other: '{{count}} points of sale, series and rules count here — move them to another outlet first.',
      deleteOutletConfirm: 'Mark “{{name}}” deleted? It leaves the screens and the lists; it can be restored.',
      deletedOutlets: 'Deleted outlets',
      deletedOn: 'deleted {{date}}',
      restoreOutlet: 'Restore',
      newOutletName: 'New outlet name',
      newOutletCode: 'Code, e.g. bar-plaza',
      outletCodeHint: 'Code: lowercase letters, digits and dashes. It is in the screen addresses and does not change once made.',
      addOutlet: 'Add outlet',
      duplicateOutletCode: 'An outlet with code “{{code}}” exists — perhaps among the deleted.',
      moveUp: 'Move up: {{name}}',
      moveDown: 'Move down: {{name}}',
      menuTitle: 'Menu categories',
      menuHint:
        'Each outlet’s categories and the Izzy groups in them. A group takes its subgroups with it unless a subgroup has its own category. A single product can be placed apart.',
      categoriesTitle: 'Categories',
      categoryName: 'Category name',
      sales365: 'Sales, 365 days',
      share: 'Share',
      uncategorised: 'No category',
      uncategorisedShow: '— show what falls here',
      uncategorisedHint:
        'Izzy groups with no category, and products with no group — Izzy’s generic codes on documents, such as a deposit or “usługa gastronomiczna”. A product with no group reaches a category only by its own placement.',
      uncategorisedWhat: 'Group or product',
      uncategorisedGroup: 'group',
      uncategorisedProduct: 'product with no group',
      uncategorisedNone: 'Nothing else falls under no category.',
      noCategoryYet: '— no category —',
      deleteCategoryConfirm: 'Delete “{{name}}”? Its groups and products go back to “No category”.',
      newCategory: 'New category',
      addCategory: 'Add category',
      groupsTitle: 'Izzy groups',
      groupsHint: 'A group’s category covers its subgroups. “Inherits” — the group counts where its nearest parent with a category does.',
      searchGroup: 'Search groups',
      onlySold: 'Only groups sold at this outlet',
      group: 'Group',
      groupSalesHint: 'Gross from bills at this outlet in the last 365 days, subgroups included.',
      category: 'Category',
      categoryFor: 'Category for: {{name}}',
      inherits: '— inherits —',
      inherited: 'from parent group: {{category}}',
      inheritedFrom: 'From group “{{group}}”',
      noGroups: 'No groups to show.',
      expand: 'Expand: {{name}}',
      collapse: 'Collapse: {{name}}',
      noGroup: 'Products with no Izzy group',
      noGroupHint: 'Izzy’s generic codes on documents — a deposit, an advance, “usługa gastronomiczna” — with no group in Izzy.',
      noGroupRule: 'category and outlet per product only — open it',
      showProducts: 'Show products: {{name}}',
      hideProducts: 'Hide products: {{name}}',
      productsTitle: 'Products placed apart',
      productsHint: 'A product with its own category or outlet counts there, whatever its group. Set it by opening its group in the tree.',
      product: 'Product',
      followsGroup: '— as group —',
      countsFor: 'Counts for',
      countsForHint:
        'The outlet this group’s sales at this outlet’s tills count for — e.g. minigolf sold at the cafe counts for recreation. A bill splits by line. Subgroups follow their group unless they have a rule of their own.',
      countsForFor: 'Counts for: {{name}}',
      followsParentOutlet: '— as parent —',
      followsGroupOutlet: '— as group —',
      staysHere: '{{name}} (here)',
      categoryThere: 'category at {{outlet}}',
      productMovesTitle: 'Products that count for another outlet',
      movedIn: 'also from other outlets’ tills',
      backToGroup: 'Back to group',
      noProductPlacements: 'No product is placed apart.',
      noProducts: 'Nothing found.',
      splitTitle: 'Products split by remark',
      splitHint:
        'A product the crew rings up as one, typing the dish in the remark — "Danie specjalne" with "ossobuco" or "udziec z sarny". Each variant is a dish and the words that mean it; a remark goes to the first variant whose word it contains. On Today and Results each variant is a product of its own.',
      splitSearch: 'Find a product by name or code',
      splitAdd: 'Split by remark',
      splitNone: 'No product is split.',
      splitLines: 'Last {{days}} days: {{lines}} lines, no variant: {{unmatched}}',
      splitRemove: 'Stop splitting',
      splitRemoveQuestion: 'Count "{{name}}" as one product again? Its variants will be removed.',
      splitVariant: 'Variant',
      splitWords: 'Words in the remark',
      splitWordsFor: 'Words for variant {{name}}',
      splitWordsHint:
        'Words separated by commas. Case and Polish letters do not matter, and part of a word is enough: "osob" catches "ossobuco" and "osobuko". Order matters — put a more specific variant higher.',
      splitDropVariant: 'Remove variant {{name}}',
      splitAddVariant: 'Add variant',
      splitNoVariants: 'No variants — every line counts as "other".',
      splitInvalid: 'Every variant needs a name and at least one word.',
      splitDuplicate: 'Variant names must differ.',
      splitRemarks: 'Remarks of the last {{days}} days',
      splitOnlyUnmatched: 'Only with no variant',
      splitRemark: 'Remark',
      splitRemarkLines: 'Lines',
      splitRemarkGross: 'Gross',
      splitNoRemarks: 'No remarks in this time.',
      splitBlank: '(no remark)',
      splitAssign: 'Put "{{text}}" in a variant',
      splitAssignTo: 'add to: {{name}}',
      splitAssignNew: 'new variant',
      // A canteen site is the outlet on catering's settings (`context: 'catering'`).
      duplicateName_catering: 'This canteen site already has a category with that name.',
      unplacedPointsOfSale_catering: 'Points of sale with no canteen site: {{names}}',
      unplacedSeries_catering: 'Series with no canteen site: {{names}}',
      unassignedGross_catering: 'Revenue with no canteen site in the last 365 days: {{gross}}',
      mappingHint_catering:
        'Which canteen site the revenue of each Izzy point of sale and each document series counts for. A change saves at once.',
      outlet_catering: 'Canteen site',
      outletFor_catering: 'Canteen site for: {{name}}',
      outletsTitle_catering: 'Canteen sites',
      outletsHint_catering: 'Name and order of the canteen sites on every catering screen.',
      outletInUse_catering_one: '{{count}} point of sale or series counts here — move it to another canteen site first.',
      outletInUse_catering_other: '{{count}} points of sale and series count here — move them to another canteen site first.',
      deleteOutletConfirm_catering: 'Mark “{{name}}” deleted? It leaves the screens and the lists; it can be restored.',
      deletedOutlets_catering: 'Deleted canteen sites',
      deletedOn_catering: 'deleted {{date}}',
      newOutletName_catering: 'New canteen site name',
      newOutletCode_catering: 'Code, e.g. ronal-w06',
      addOutlet_catering: 'Add canteen site',
      duplicateOutletCode_catering: 'A canteen site with code “{{code}}” exists — perhaps among the deleted.',
      menuHint_catering:
        'Each canteen site’s categories and the Izzy groups in them. A group takes its subgroups with it unless a subgroup has a category of its own. A single product can be placed apart.',
      onlySold_catering: 'Only groups sold at this canteen site',
      groupSalesHint_catering: 'Gross from bills at this canteen site in the last 365 days, subgroups included.',
      noGroupRule_catering: 'category per product only — open it',
      productsHint_catering:
        'A product with its own category counts there, whatever its group. Set it by opening its group in the tree.',
    },
  },
  catering: {
    nav: {
      today: 'Today',
      results: 'Results',
      controls: 'Controls',
      settlement: 'Settlement',
      settings: 'Settings',
    },
    payments: {
      internal: 'Other internal settlement',
      internalNote: 'not revenue',
      internalHint:
        'Internal settlement for a reason other than the employee card. It is not revenue, so it is not in the shares above.',
    },
    settlement: {
      title: 'Settlement',
      subtitle: 'A canteen\u2019s month on the employee card: the card report for the employer, and the invoice figures per company.',
      site: 'Canteen',
      siteNoCard: '{{name}} (no employee card)',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      noAccess: 'You cannot settle the canteens. The catering manager does it.',
      noSites: 'Catering has no canteen yet.',
      reportTitle: 'Card report',
      reportHint: 'The month\u2019s employee-card meals per card, for the employer. It holds card and employee numbers.',
      download: 'Download card report (.xlsx)',
      downloading: 'Downloading…',
      cards: 'Cards',
      reportTotal: 'Report total',
      lines: 'Card meal lines',
      unrated_one: '{{count}} card meal line has no VAT rate. Its share cannot be split into nett and VAT.',
      unrated_other: '{{count}} card meal lines have no VAT rate. Their share cannot be split into nett and VAT.',
      noCardSales: 'No employee-card meal at this canteen in this month.',
      mixTitle: 'Card sales by VAT rate',
      mixRate: 'VAT rate',
      mixGross: 'Card sales, gross',
      mixShare: 'Share',
      noRate: 'No rate',
      companiesTitle: 'Companies the employer bills',
      companiesHint: 'Type each company and the gross the employer gave for it. Together they should make the report total.',
      companiesOffered: 'Last month\u2019s companies. Type this month\u2019s gross for each.',
      savedAt: 'Saved {{at}}.',
      company: 'Company',
      companyOf: 'Company {{n}}',
      gross: 'Gross',
      grossOf: 'Gross of company {{n}}',
      remove: 'Remove',
      removeOf: 'Remove company {{n}}',
      typedTotal: 'Companies together',
      typedDifference: 'The companies differ from the report total by {{difference}} zł.',
      add: 'Add company',
      undo: 'Undo',
      invalidRows: 'Each row needs a company once and a gross with at most two decimals.',
      invoicesTitle: 'Invoice figures',
      invoicesHint: 'Each company\u2019s gross split across the VAT rates by the month\u2019s card sales. The rates add up to the company\u2019s gross, nett and VAT to each rate\u2019s gross.',
      difference: 'The saved companies differ from the report total by {{difference}} zł. Check the employer\u2019s figures.',
      noCompanies: 'No companies saved for this month yet.',
      rate: 'VAT rate',
      nett: 'Nett',
      vat: 'VAT',
      total: 'Total',
      allCompanies: 'All companies',
    },
    settings: {
      title: 'Settings',
      subtitle: 'The databases catering reads, where revenue counts — canteen sites, points of sale, series and menu categories — and which settlement reasons are the employee card.',
      readOnly: 'View only — the catering manager makes changes.',
      cardTitle: 'Employee card — {{name}}',
      cardHint:
        'This database’s internal-settlement reasons. The ticked ones are the employee card: their amount is revenue, invoiced to the employer. The others are not revenue.',
      isCard: 'Employee card',
      isCardOf: 'Employee card: {{name}}',
      reason: 'Reason',
      code: 'Code',
      bills: 'Bills in a year',
      noReasons: 'This database has no internal-settlement reasons.',
      undo: 'Undo',
    },
  },
  navBar: {
    more: 'More',
    waiting_one: '{{count}} waiting',
    waiting_other: '{{count}} waiting',
  },
  cashier: {
    history: {
      documentsSince: 'You can search documents from {{date}}.',
    },
    documentKind: {
      receipt: 'Receipt',
      invoice: 'Invoice',
      correction: 'Correction',
    },
    documents: {
      noLines: 'No lines for this document.',
    },
    closeFlow: {
      drawerStart: 'In the drawer',
      drawerStartHint: 'at the start of the day, after the handover',
      takingsToday: "The day's cash takings",
      filter: 'Filter',
      filterActive: 'Filter ({{n}})',
      clearFilters: 'Clear filters',
      addAnother: 'Add another report',
      settleSummary: 'Cash takings {{amount}}',
      printerSuggestion: 'system: {{total}} gross',
      unassignedSeries_one: 'Series {{series}} has no printer set — set it in Settings.',
      unassignedSeries_other: 'Series {{series}} have no printer set — set them in Settings.',
      takingsTitle: 'Cash takings of the day',
      countTitle: 'Cash in the drawer',
      countWhole: 'all the cash in the drawer, coins too',
      countFirst: 'The difference shows once you count.',
      countAfterSlips: 'The difference shows once the slips are in.',
      handoverSection: 'Handover to the main desk',
      handoverAmount: 'To hand over',
      bagNumber: 'Bag number (optional)',
      bagLine: 'bag {{bag}}',
      handoverAccepted: 'accepted by the main desk',
      handoverWaiting: 'waiting for the main desk',
      noMainDesk: 'This desk has no main desk set — you submit the report without a handover.',
      commentHint: 'Briefly, what happened, e.g. "wrong change given".',
      afterSubmitHandover: 'After you submit, the manager checks the report and {{amount}} waits for the main desk to accept it.',
      afterSubmitOwn: 'After you submit, the day is closed.',
      afterSubmitOwnHandover: 'After you submit, the day is closed and {{amount}} waits for the main desk to accept it.',
      ownAccepts: 'You manage this desk — the report is accepted at once.',
      submitHandover: 'Submit and hand over {{amount}}',
      blocker: {
        slips: 'First type in the slips: the fiscal report and the terminal.',
        blocked: 'You cannot submit yet — see the message at the top of the page.',
        invalid: 'Fix the marked fields, then submit.',
        count: 'Type how much cash you counted.',
        comment: 'Explain the differences in the comment.',
      },
      doneAccepted: 'Report submitted and accepted.',
      doneHandover: 'The handover of {{amount}} waits for the main desk to accept it.',
      progress: 'Steps of the day close',
      stepDone: '(done)',
      stepLocked: '(slips first)',
      step: {
        settle: 'Settle',
        slips: 'Slips',
      },
      short: {
        settle: 'Settle',
        slips: 'Slips',
      },
      next: 'Next: {{step}}',
      toNextShort: 'Next: {{date}}',
      fiscalSummary_one: '{{count}} report · {{total}}',
      fiscalSummary_other: '{{count}} reports · {{total}}',
      terminalSummary_one: '{{count}} terminal · {{total}}',
      terminalSummary_other: '{{count}} terminals · {{total}}',
      problem: {
        terminalTotal: '{{device}}: type the total from the slip.',
        missing: 'No report: {{device}}.',
        printedAt: '{{device}}: type the print time.',
        total: '{{device}}: type the gross total from the slip.',
        rateSum: 'The rates give {{sum}}, the slip {{total}}. Check nett and VAT.',
        vat: '{{device}}, rate {{rate}}: VAT should be about {{expected}}.',
      },
      noRates: 'No rates.',
      rateSumOff: 'The rates give {{sum}}, the slip {{total}}. Check nett and VAT against the slip.',
      expectedNegative: 'The result is below zero: more cash was sent than the drawer held.',
      cashMatches: 'It matches',
      cashMatchesHint: 'The drawer holds what it should.',
      cashShort: '{{amount}} short',
      cashOver: '{{amount}} over',
      cashRecount: 'Count again. If it holds, explain it in the comment.',
      showCardPayments: 'Show card payments ({{n}})',
      closeDay: 'Close {{date}}',
      staleHint: 'Wait for new data from izzy, then come back here.',
      paperTitle: 'Invoices and corrections: do you have paper copies?',
      afterSubmit: 'After you submit, the manager checks the report. You can change it only if they return it.',
      doneTitle: 'Report submitted.',
      doneHint: 'The manager will check it.',
      summaryTitle: 'Day summary',
      cashLine: 'Cash: not counted',
      cashLineOk: 'The cash agrees',
      cashLineDiff: 'Cash difference: {{amount}}',
      cashFigures: 'Counted {{counted}} · should be {{expected}}',
      explanation: "Cashier's explanation",
      noExplanation: 'No explanation.',
      changesTitle: 'Changes in version {{number}} (since version {{previous}})',
      noChanges: 'The cashier changed nothing.',
      changeCounted: 'Counted: {{from}} → {{to}}',
      changeFiscal: 'Fiscal reports: {{from}} → {{to}} (reports: {{fromCount}} → {{toCount}})',
      changeFiscalDetail: 'Fiscal reports: rates or print time changed, same total ({{total}})',
      changeTerminalDetail: 'Terminals: print time changed, same total ({{total}})',
      changeTerminal: 'Terminals: {{from}} → {{to}} (reports: {{fromCount}} → {{toCount}})',
      change: {
        dropped: 'Taken out of the day',
        restored: 'Put back',
        pulledIn: 'Pulled into the day',
        unpulled: 'Addition undone',
        checked: 'Ticked "on paper"',
        unchecked: 'Unticked "on paper"',
        paymentChanged: 'Payment changed',
        paymentUndone: 'Payment change undone',
      },
      return: 'Return',
      returnHint: 'The cashier sees the reason on this day.',
      acceptNext: 'Accept and next',
      dropFor: 'Take out of the day: {{ref}}',
      restoreFor: 'Put back: {{ref}}',
      unpullFor: 'Undo addition: {{ref}}',
      toHandovers: 'Accept takings at the main desk',
    },
    transferFlow: {
      bagShort: 'bag {{bag}}',
      noBagShort: 'no bag',
      corrected: 'Corrected after acceptance',
      correctedLine: 'Accepted {{was}} ({{name}}, {{at}}). Now {{amount}}, difference {{difference}}.',
      keepApart: 'The main desk does not count this amount until you accept it again.',
      dayWhole: 'A day\'s takings are accepted only whole. Return the day: the cashier corrects it, and the same handover comes back with the right amount.',
      dayNotYours: 'You cannot return this day. Ask the desk\'s manager to correct the report.',
      returnReasonLabel: 'What does not match (required)',
      returnNote: 'Counted {{counted}}, handover {{amount}}. {{text}}',
      returnDay: 'Return the day for correction',
      countQuestion: 'How much did you count?',
      refuseWhole: 'A transfer is accepted only whole. Refuse it: the sender sends the cash again, with the amount you counted together.',
      refuseNotYours: 'You may not refuse this transfer. Ask the main cashier.',
      refuse: 'Refuse transfer',
      acceptAmount: 'Accept {{amount}}',
      sendTitle: 'Hand over cash: {{name}}',
      moveTitle: 'Move from: {{name}}',
      floatTitle: 'Float a desk from: {{name}}',
      toHandOver: 'To hand over',
      where: 'Where to?',
      from: 'From',
      to: 'To',
      bagOptional: 'Bag number (optional)',
      noteHint: 'An amount other than the one to hand over needs a note.',
      next: 'Next',
      back: 'Change',
      confirmTitle: 'Check before sending',
      sendAmount: 'Send {{amount}}',
      sendFinal: 'A sent transfer cannot be undone. The main cashier counts it on receipt.',
      moveFinal: 'A move cannot be undone. It is booked at once, without a count.',
      countOff: 'Counted {{counted}}, should be {{amount}}: {{gap}}.',
      missing: '{{amount}} short',
      surplus: '{{amount}} over',
    },
    denominations: {
      title: 'Count by denomination',
      short: 'By notes',
      hint: 'How many of each banknote; coins as one amount.',
      coins: 'Coins',
      total: 'Total',
      countOf: 'Number of {{note}} notes',
      less: 'One {{note}} note fewer',
      more: 'One {{note}} note more',
      use: 'Use {{amount}}',
      notStored: 'A counting aid. Only the total is saved.',
    },
    nav: {
      closeShort: 'Report',
      reportsShort: 'History',
      close: 'Day report',
      reports: 'Report history',
      overview: 'Overview',
      main: 'Main desk',
      settings: 'Settings',
    },
    desk: 'Cash desk',
    inactiveDesk: '{{name}} (inactive)',
    noSalesDesks: 'No cash desks to show.',
    notAvailable: 'You have no access to the Cashier module.',
    invalidAmount: 'Type an amount, e.g. 12.50.',
    mainDesk: 'Main desk',
    stashLabel: '{{company}} — stash {{stash}}',
    inactiveMark: '(inactive)',
    state: {
      none: 'No report',
      draft: 'Draft',
      submitted: 'Submitted',
      returned: 'Returned',
      accepted: 'Accepted',
    },
    level: {
      warning: 'Warning',
      error: 'Error',
      blocking: 'Blocks',
    },
    check: {
      FISCAL_NOT_VALID: 'Fiscal report missing or does not add up.',
      TERMINAL_NOT_VALID: 'No terminal report.',
      SYSTEM_CASH_NOT_VALID: 'Cash: slips {{declared}}, izzy {{system}}.',
      SYSTEM_CARD_NOT_VALID: 'Card: slips {{declared}}, izzy {{system}}.',
      SYSTEM_TOTAL_NOT_VALID: 'Total: slips {{declared}}, izzy {{system}}.',
      SMALL_CASH_DIFF: 'Small cash difference: counted {{counted}}, should be {{expected}}.',
      LARGE_CASH_DIFF: 'Large cash difference: counted {{counted}}, should be {{expected}}.',
      PREVIOUS_REPORT_NOT_APPROVED: 'The day before waits for the manager to accept it.',
      NO_PREVIOUS_REPORT: 'This is the desk\'s first report.',
      MISSING_REPORT: 'First close the days with sales and no report: {{days}}.',
      FISCAL_DATE_MISMATCH: 'The fiscal report is dated another day.',
      SYSTEM_DATA_STALE: 'The izzy data ({{asOf}}) is older than the fiscal report ({{printedAt}}).',
      DOCUMENT_COUNTED_TWICE: 'A document also counted in another report: {{documents}}. Undo the addition, or ask for that day to be corrected.',
      FISCAL_MISSING: 'No fiscal report: {{devices}}.',
      TERMINAL_MISSING: 'No terminal report: {{devices}}.',
    },
    slips: {
      hint: 'Copy the amounts from the slips: the gross total from the fiscal report, the total from the terminal’s closing slip. A zero slip: type 0. The system fills in the VAT rates.',
      fiscalTitle: 'Fiscal reports',
      terminalTitle: 'Terminals',
      terminalHint: 'total from the day’s closing slip',
      printed: 'printed {{at}}',
      timeChange: 'change',
      timeDone: 'done',
      addReport: 'Add another report: {{device}}',
      rates: 'VAT rates:',
      ratesFromSystem: 'VAT rates from the system:',
      ratesAgree: 'add up to the gross total',
      ratesSumOff: 'add up to {{sum}}, the gross total is {{total}}',
      ratesVatOff: 'the VAT of one rate does not match',
      editRates: 'Change rates',
      checkRates: 'Check rates',
      showRates: 'Show rates',
      hideRates: 'Fold rates',
      ratesHint: 'Rates from the system, from the sales on this printer. Correct only the ones that differ from the slip.',
    },
    payments: {
      change: 'Change the payment method',
      changeFor: 'Change the payment method: {{ref}}',
      undo: 'Undo the payment change',
      undoFor: 'Undo the payment change: {{ref}}',
      undoShort: 'undo',
      title: 'How the document of {{amount}} was paid',
      izzy: 'In izzy: {{payments}}.',
      onlyHere: 'The change is for this close only; izzy keeps the document as it is.',
      methods: 'Payment method',
      split: 'Split',
      remaining: 'Left to split: {{amount}}',
      addsUp: 'Adds up to the document.',
      unreadable: 'Correct the amount that does not read.',
      same: 'The same as in izzy: no change.',
      reason: 'Why (required, the manager sees it)',
      apply: 'Change',
      was: 'in izzy: {{payments}}',
      none: 'no payment',
      kind: {
        cash: 'cash',
        card: 'card',
        transfer: 'transfer',
      },
    },
    handover: {
      wholeTakings: 'the day’s whole cash takings',
      acceptedBy: 'received: {{name}}, {{at}}',
      correctedFrom: 'Corrected after it was received: it was {{amount}}, received by {{name}}, {{at}}. Waiting to be received again.',
      reaccept: 'Received before: {{was}}. After the submit the same handover: {{amount}} ({{difference}}), to be received again at the main desk.',
      inTransit: 'The previous submit sent {{was}}. After this one the same handover changes to {{amount}}.',
      stillWaiting: 'The handover of the previous submit waits to be received at the main desk.',
      nothing: 'The cash takings are not above zero: nothing is handed over today.',
    },
    dayBar: {
      version: 'Version {{number}}',
      nextVersion: 'Version {{number}} in progress',
      submitted: 'Submitted',
      accepted: 'Accepted',
      returned: 'Returned',
      reopened: 'Reopened',
      whatHappens: 'What happens',
      laterClosed: 'This day changes only after the later closed day is opened: {{date}}. The cash of a day goes into the days after it.',
      reopenTip: 'Only the desk’s last closed day can be reopened. The reason goes into the day’s history.',
      acceptTip: 'You accept as the desk manager. The handover waits to be received at the main desk.',
      reopenHint: 'The cashier sees the reason on this day. If the correction changes the takings, the handover goes back to be received again at the main desk.',
      windowLabel: 'The day’s sales window',
    },
    finish: {
      checkTitle: 'Check',
      checkHint: 'What izzy, the terminal and the fiscal report say. Nothing to type here.',
      title: 'To do',
      progress: 'Step {{step}} of {{total}}',
      hint: 'In order, from the top. What glows is next.',
      count: 'Count the cash',
      paper: 'Invoices and corrections on paper',
      paperHint: 'Tick the ones you have a copy of. It does not hold the submit.',
      paperCount: '{{checked}} of {{total}}',
      comment: 'Comment',
      finish: 'Hand over the cash and submit',
      finishNoHandover: 'Submit the report',
      now: 'now',
      optional: 'optional',
      required: 'required',
      added: 'added',
      afterSlips: 'after the slips',
      afterCount: 'after the count',
      last: 'last step',
      waits: 'waiting',
      countedAgrees: 'counted {{amount}} · agrees',
      countedDiffers: 'counted {{amount}} · difference {{difference}}',
    },
    dayPanel: {
      label: 'Day panel',
      documents: 'Day’s documents',
      history: 'History and comments',
      title: 'Documents and history',
      open: 'Documents and history ({{n}})',
      noHistory: 'The history starts with the day’s first save.',
      notSubmitted: 'Not submitted yet.',
    },
    summary: {
      slips: 'Slips',
      fiscalSlip: '{{device}}, fiscal report',
      terminalSlip: '{{device}}, terminal',
      paymentChanges_one: 'Payment changed: {{count}} document',
      paymentChanges_other: 'Payment changed: {{count}} documents',
      paper: 'Paper copies of invoices and corrections: {{ticked}} of {{count}}',
      showDetails: 'Show details',
      hideDetails: 'Hide details',
      allCards: 'every card',
      fiscalSales: 'fiscal sales',
      fromSlips: 'from the slips',
    },
    reopen: {
      titleReopened: 'Day reopened: version {{number}}',
      titleReturned: 'Day returned for correction: version {{number}}',
      reason: '{{name}}, {{at}}: “{{text}}”',
      handoverAccepted: 'The main desk has already received this day’s handover: {{amount}}. If the correction changes the cash takings, the same handover takes the new amount and goes back to the main desk to be received again; until then the main desk does not count it. No new entry is made.',
      handoverSent: 'This day’s handover ({{amount}}) waits at the main desk. After the submit it takes the corrected takings.',
    },
    settle: {
      waiting: 'Waiting for the fiscal and terminal reports',
      takingsPending: 'worked out once the slips are in',
      takingsSame: 'from the slips · the same as the cash in izzy',
      takingsLess: 'from the slips · {{amount}} less than the cash in izzy',
      takingsMore: 'from the slips · {{amount}} more than the cash in izzy',
      more: '{{amount}} more',
      less: '{{amount}} less',
      showCashPayments: 'Show cash payments ({{n}})',
      candidates_one: 'One document has exactly {{amount}} {{method}}. If the guest paid otherwise, change its payment method:',
      candidates_other: '{{count}} documents have exactly {{amount}} {{method}}. If the guest paid otherwise, change the right one’s payment method:',
      byMethod: {
        cash: 'in cash',
        card: 'by card',
      },
      handoverWaits: 'Opens once you type the cash counted.',
      handoverWaitsSlips: 'Opens once the slips are in and the cash is counted.',
      insert: 'Insert:',
      quote: '“{{text}}”',
      slips: {
        title_both: 'First type in the slips',
        title_fiscal: 'First type in the fiscal report',
        title_terminal: 'First type in the terminal report',
        body_both: 'The fiscal report ({{fiscal}}) and the terminal report ({{terminal}}) are missing. Without them there are no takings to work out and no report to submit.',
        body_fiscal: 'The fiscal report is missing: {{fiscal}}. Without it there are no takings to work out and no report to submit.',
        body_terminal: 'The terminal report is missing: {{terminal}}. Without it there are no takings to work out and no report to submit.',
        to_both: 'Type in the slips',
        to_fiscal: 'Type in the fiscal report',
        to_terminal: 'Type in the terminal report',
      },
      agree: {
        title: 'Everything agrees',
        body: 'The terminal and the fiscal report give the same amounts as izzy. This much cash came into the drawer.',
      },
      method: {
        title_card: 'A card payment recorded in izzy as cash',
        body_card: 'The terminal has {{amount}} more than the cards in izzy, and the sales total agrees. It is the same sale, recorded with another payment method.',
        comment_card: 'A card payment of {{amount}} recorded in izzy as cash.',
        title_cash: 'A cash payment recorded in izzy as card',
        body_cash: 'The terminal has {{amount}} less than the cards in izzy, and the sales total agrees. It is the same sale, recorded with another payment method.',
        comment_cash: 'A cash payment of {{amount}} recorded in izzy as card.',
        next_card: 'Find it among the cash payments and change its payment method here or in izzy.',
        next_cash: 'Find it among the card payments and change its payment method here or in izzy.',
      },
      fiscal: {
        title_less: 'The fiscal report is short by {{amount}}',
        body_less: 'Izzy has {{amount}} more fiscal sales than the printer. The cards agree, so it is cash: a receipt did not print, or a document is from another day.',
        next_less: 'Count the drawer: {{amount}} over means the receipt did not print.',
        comment_less: 'The fiscal report is {{amount}} short of izzy.',
        title_more: 'The fiscal report has {{amount}} more than izzy',
        body_more: 'The printer has {{amount}} more sales than izzy. The cards agree, so it is cash: a receipt issued outside izzy, or a document from another day.',
        next_more: 'If a document is from another day, pull it into this day.',
        comment_more: 'The fiscal report has {{amount}} more than izzy.',
      },
      cardSale: {
        title_more: 'A card sale of {{amount}} is not in izzy',
        body_more: 'The terminal and the fiscal report both have {{amount}} more than izzy. The cash agrees. Usually a document from another day or another desk.',
        next_more: 'Pull that document in, or explain it below.',
        comment_more: 'A card sale of {{amount}} is on the slips and not in izzy.',
        title_less: 'Izzy has a card sale of {{amount}} that is not on the slips',
        body_less: 'The terminal and the fiscal report both have {{amount}} less than izzy. The cash agrees. Usually a document from another day.',
        next_less: 'Leave it out of this day, or explain it below.',
        comment_less: 'A card sale of {{amount}} is in izzy and not on the slips.',
      },
      two: {
        title: 'Two differences to explain',
        body: 'Terminal: {{terminal}} than the cards in izzy. Fiscal report: {{fiscal}} than the fiscal sales in izzy.',
        next: 'Check each one, starting with the terminal.',
      },
      flow: {
        title: 'From izzy to the takings',
        izzy: 'Izzy',
        izzyHint: 'the day’s sales by payment method',
        fiscal: 'Fiscal',
        nonFiscal: 'Non-fiscal',
        nonFiscalNote: 'Non-fiscal: {{list}}. Not on the fiscal report; paid by card, they are on the terminal.',
        documents_one: '{{count}} document',
        documents_other: '{{count}} documents',
        legendTerminal: 'held against the terminal',
        legendFiscal: 'held against the fiscal report',
        terminal: 'Terminal',
        terminalHint: 'every card: from receipts and from invoices',
        terminalSlip: 'Terminal’s closing slip',
        terminalIzzy: 'Card in izzy, total',
        fiscalTitle: 'Fiscal report',
        fiscalHint: 'fiscal sales only, no non-fiscal documents',
        fiscalSlip: 'Daily report',
        fiscalIzzy: 'Fiscal sales in izzy',
        cash: 'Cash takings',
        cashHint: 'from the slips: the fiscal report less the fiscal cards',
        cashFiscal: 'Fiscal report',
        cashCard: 'Fiscal card',
        cashCardHint: 'terminal {{terminal}} − non-fiscal {{nonFiscal}}',
        cashCardMissing: 'terminal: not typed in',
        cashMoneyless: 'Not to the drawer',
        cashNonFiscal: 'Non-fiscal cash',
        fromIzzy: 'from izzy',
        cashTakings: 'Cash takings',
        cashIzzy: 'Cash in izzy',
        notEntered: 'not typed in',
        enterStep: 'type it in at step {{n}}',
        agrees: 'agrees',
        moreThanIzzy: '{{amount}} more than izzy',
        lessThanIzzy: '{{amount}} less than izzy',
        waiting: 'waiting for the slips',
        sameAsIzzy: 'same as izzy',
        cause_terminal: 'This difference comes from the terminal, above. Do not look for it separately.',
        cause_fiscal: 'This difference comes from the fiscal report, above. Do not look for it separately.',
        cause_both: 'This difference comes from the terminal and the fiscal report, above. Do not look for it separately.',
      },
    },
    split: {
      cash: 'Cash',
      card: 'Card',
      transfer: 'Transfer',
      other: 'Not to the drawer (e.g. to the room)',
      total: 'Total',
    },
    balance: {
      expected: 'Should be in the drawer',
      counted: 'Counted',
      difference: 'Difference',
    },
    documentTag: {
      correction: 'correction',
      invoice: 'invoice',
      nonFiscal: 'non-fiscal',
      pulledIn: 'pulled in',
      otherSeries: "another desk's series",
      settledBy: 'settled at: {{desk}}',
      dropped: 'dropped from the day',
      changed: 'payment changed',
    },
    commentKind: {
      note: 'Comment',
      return: 'Return',
      reopen: 'Reopen',
    },
    bookKind: {
      deposit: 'Bank deposit',
      withdrawal: 'Bank withdrawal',
      income: 'Income',
      expense: 'Expense',
      loan: 'Loan',
      repayment: 'Loan repayment',
      funding: 'Funding',
      defunding: 'Funding return',
      'loan-given': 'Loan to stash A',
      'loan-returned': 'Loan repaid by stash A',
      move: 'Move to B',
      'move-in': 'Move from B',
      'transfer-in': 'Transfer received',
      'transfer-out': 'Transfer',
      payout: 'Payout',
      'payout-return': 'Payout return',
    },
    paymentKind: {
      cash: 'Cash',
      card: 'Card',
      transfer: 'Transfer',
      none: 'Not to the desk',
    },
    close: {
      beforeOpening: "This day is before the desk's first day ({{date}}) — read only. Change the first day in Settings.",
      future: 'This day has not begun yet — read only.',
      noDocumentsMatch: 'No document matches the filters.',
      searchRef: 'Search number',
      allKinds: 'All',
      anyPayment: 'Any',
      tags: 'Flags',
      documentType: 'Type',
      title: 'Day report',
      subtitle: 'Fiscal report, terminal, then settle and submit',
      day: 'Day',
      toNext: 'Next day to close: {{date}}',
      window: 'Sales from {{from}} to {{to}}',
      saveInvalid: 'Not saved — fix the marked fields.',
      saved: 'Saved {{time}}',
      saveFailed: 'Not saved: {{message}}',
      saveFirst: 'The draft could not be saved. Try again.',
      notYours: 'You do not close days at this desk — read only.',
      inactive: 'inactive',
      noPrinters: 'This desk has no active printer.',
      noFiscal: 'No report from this printer.',
      fiscalNumber: '{{name}} — report {{number}}',
      printedAt: 'Print time',
      totalGross: 'Total gross',
      removeReport: 'Remove',
      rate: 'Rate',
      rateLabel: '{{letter}} {{percent}} %',
      nett: 'Nett',
      vat: 'VAT',
      gross: 'Gross',
      nettFor: 'Nett, rate {{rate}}',
      vatFor: 'VAT, rate {{rate}}',
      vatOff: 'VAT should be about {{expected}}',
      rateSum: 'Sum of rates',
      suggestionNoLines_one: '{{count}} document without lines — the suggestion is incomplete.',
      suggestionNoLines_other: '{{count}} documents without lines — the suggestion is incomplete.',
      suggestionUnmapped: 'Rate {{rate}} % has no printer letter: {{gross}} gross left out of the suggestion.',
      fillFromSystem: 'Fill from the system',
      noTerminals: 'This desk has no active terminal.',
      noTerminalReports: 'No report from this terminal.',
      terminalTotal: 'Total',
      systemAsOf: 'izzy data from {{time}}',
      noSystemData: 'No izzy data for this desk.',
      documentsHint_one: '{{count}} document',
      documentsHint_other: '{{count}} documents',
      noDocuments: 'No sales documents on this day.',
      documentsShown: '{{shown}} of {{count}}',
      paidWith: 'Payment',
      document: 'Document',
      createdAt: 'Created',
      drop: 'Drop from the day',
      dropReason: 'Reason to drop',
      restore: 'Restore',
      unpull: 'Undo addition',
      pullInTitle: 'Add a document from another day or series',
      documentNumber: 'Document number',
      documentNumberHint: 'e.g. 1/10 or 12/9/K/2026',
      seriesOfDesk: 'Series {{series}} — assigned to: {{desk}}',
      seriesOfNoDesk: 'Series {{series}} — not assigned',
      searchLimit: 'The latest {{count}} shown. Type more of the number to find older ones.',
      nothingFound: 'No such document.',
      alreadyInDay: 'Already in this day',
      pullIn: 'Pull in',
      pullInReason: 'Reason to pull in',
      checksPending: 'Saving your changes — what is below may still change.',
      submit: 'Submit report',
      return: 'Return to the cashier',
      returnReason: 'Reason to return',
      reopen: 'Reopen',
      reopenReason: 'Reason to reopen',
      revisions: 'Versions',
      revisionLine: 'Version {{number}} — {{name}}, {{at}}',
      comments: 'Comments',
      noComments: 'No comments.',
      addComment: 'Add a comment',
      sendComment: 'Send comment',
    },
    reports: {
      notSubmitted: 'Not submitted',
      floatTarget: 'Target {{amount}}',
      title: 'Report history',
      subtitle: 'Every day of the desk: its state, cash, handover and notes. Click a day to see it.',
      subtitleAcceptor: 'Every day of the desk: its state, cash, handover and notes. Click a day to see it and accept it at once.',
      allDesks: 'All desks',
      deskPending: '{{name}} · {{count}} to accept',
      filters: 'Which days',
      filter: {
        pending: 'To accept · {{n}}',
        notes: 'With notes · {{n}}',
        all: 'All days · {{n}}',
      },
      empty: {
        pending: 'Nothing waits to be accepted.',
        notes: 'No day has notes.',
        all: 'No days this month.',
      },
      col: {
        day: 'Day',
        state: 'State',
        takings: 'Cash takings',
        difference: 'Difference',
        handover: 'Handover',
        after: 'In the drawer',
        notes: 'Notes',
      },
      state: {
        accepted: 'Accepted',
        pending: 'To accept',
        submitted: 'Submitted',
        returned: 'Returned for correction',
        draft: 'In progress',
        missing: 'No report',
        notStarted: 'Not started',
      },
      handover: {
        open: '—',
        openSub: 'day not closed',
        held: 'Held',
        heldSub: 'comes back once the day is corrected',
        none: 'No handover',
        noneSub: 'nothing to hand over',
        sent: 'On its way',
        sentSub: 'sent {{at}}',
        accepted: 'Received',
        acceptedSub: '{{name}}, {{at}}',
      },
      note: {
        returned: 'To correct',
        explanation: 'Explanation',
      },
      totalsOf: 'Total: {{month}}',
      handedTotal: 'handed over {{amount}}',
      bulk: 'Accept without notes · {{n}}',
      bulkQuestion_one: 'Accept {{count}} day without notes?',
      bulkQuestion_other: 'Accept {{count}} days without notes?',
      bulkConfirm: 'Accept',
      progress: 'Accepting {{done}} of {{total}}…',
      result: 'Accepted {{accepted}} of {{total}}.',
      failed: 'Not accepted:',
      skipped: 'skipped, because an earlier day at this desk is not accepted',
      pendingElsewhere_one: '{{count}} day waits to be accepted in another month.',
      pendingElsewhere_other: '{{count}} days wait to be accepted in another month.',
      showMonth: 'Show {{month}}',
      outstanding: {
        title: 'Hand over cash still owed',
        line: 'The desk has {{amount}} to hand over.',
      },
      float: {
        banner: 'The main desk sent a float: {{amount}}',
        bannerHint: 'count it and accept it before you close the day',
        accept: 'Accept the float',
        title: 'Accept the float',
        from: 'from {{desk}}',
        should: 'Sent',
        footnote: 'The float accepted goes into the cash of the day you close.',
        accepted: 'Float accepted: {{amount}}.',
        refused: 'Float refused. The main desk sends it again.',
      },
      drawer: {
        submittedBy: 'submitted by {{name}}, {{at}}',
        acceptedBy: 'accepted by {{name}}, {{at}}',
        version: 'Version {{number}}',
        expected: 'Cash expected',
        counted: 'Cash counted',
        after: 'In the drawer after the handover',
        handover: 'Handover to the main desk',
        checks: 'System notes',
        returnedBecause: 'Returned ({{name}}): {{reason}}',
        explanation: 'The cashier\'s explanation',
        comments: 'Comments',
        openTitle: 'The day is still open',
        openBody: 'The cashier has not submitted it yet.',
        returnedTitle: 'The day is back with the cashier',
        returnedBody: 'The cashier is correcting it; submitted again, it comes back here to be accepted.',
        accept: 'Accept',
        return: 'Return for correction',
        returnWith: 'Return with this note',
        returnReason: 'What to correct (the cashier reads it)',
        then: 'Then: {{day}}',
        nextOne: 'Next: {{day}}.',
        nothingMore: 'Nothing else waits.',
        accepted: '{{day}} accepted.',
        returned: '{{day}} returned for correction.',
        own: 'This is your report: someone else accepts it — four eyes.',
        fix: 'Correct it in the day report',
        fixReturned: 'The manager returned the day. Correct it and submit it again — the handover comes back with the right amount.',
        fixOpen: 'The day waits to be closed.',
        openReport: 'Open the whole report',
      },
    },
    transfers: {
      amount: 'Amount',
      bag: 'Bag number',
      note: 'Note',
      noteRequired: 'Note (required: the amount is not the one to hand over)',
      noMainDesk: 'This desk has no main desk set, so it cannot send cash. Ask a general manager to set it in Settings.',
      bookedAtOnce: 'A move between main desks is booked at once, with no count.',
    },
    main: {
      title: 'Main desk',
      noMainDesk: 'No main desk to show.',
      open: 'Open',
      closed: 'Closed',
      bookTitle: 'Book',
      lineCount_one: '{{count}} line',
      lineCount_other: '{{count}} lines',
      noLines: 'No lines this month.',
      total: 'Month total',
      lp: 'No.',
      date: 'Date',
      document: 'Document',
      kind: 'Kind',
      description: 'Description',
      counterparty: 'Counterparty',
      in: 'In',
      out: 'Out',
      actions: 'Actions',
      amount: 'Amount',
      editLine: 'Change line',
      deleteLine: 'Delete line',
      editDate: 'Change the date in the main desk',
      openPayouts: 'Open payouts',
      openReport: 'Day report {{day}}',
      kindTakings: 'Desk takings',
      groupTakings: 'Group takings by desk',
      expandGroup: 'Show takings: {{desk}}',
      collapseGroup: 'Hide takings: {{desk}}',
      takingsTotal_one: 'Total takings · {{count}} day',
      takingsTotal_other: 'Total takings · {{count}} days',
      bank: 'Bank · company account',
      line: {
        takings: 'Takings {{day}}',
        takingsBag: 'Takings {{day}} · bag {{bag}}',
        bag: 'bag {{bag}}',
        transfer: 'Transfer',
        payroll: 'Payroll {{period}} · {{parts}}',
      },
      balanceTitle: 'Cash balance',
      balanceOn: 'Balance on {{date}}',
      balanceNow: 'Balance now',
      balanceAtEnd: 'Balance at month end',
      payoutsDone: 'Payouts for {{period}} · all paid',
      payoutsLink: 'Payouts for {{period}} · {{n}} to pay',
      payoutsBack_one: '{{count}} return',
      payoutsBack_other: '{{count}} returns',
      closedBy: 'Closed: {{name}}, {{at}}.',
      closesFrom: 'The month is not over yet; it closes from {{date}}.',
      provisional: 'provisional',
      provisionalHint: 'Provisional: after this line the desk falls below zero — on a stash B desk, below what it may borrow from stash A. Add an earlier line, such as a loan, that covers the gap.',
      shortfall: 'Provisional lines: {{amount}} short. Add an earlier line, such as a loan, that covers it — then they stop being provisional.',
      borrowedByB: 'Stash B borrowed',
      cashInHand: 'Actually in hand',
      closeShortfall: 'The month does not close: there are provisional lines, {{amount}} short.',
      closeMonth: 'Close month',
      reopenMonth: 'Reopen month',
      reopenWho: 'Only a general manager reopens a month.',
      closeTitle: 'Close: {{month}}',
      closeConfirm: 'Confirm {{amount}} and close',
      closeCarries: "This amount becomes the next month's opening balance.",
      closeInTransit_one: '{{count}} transfer ({{amount}}) waits to be received. After the close you receive it in the next month.',
      closeInTransit_other: '{{count}} transfers ({{amount}}) wait to be received. After the close you receive them in the next month.',
      closeFinal: "After the close this month's lines do not change. Only a general manager reopens a month.",
      closedTitle: 'Month closed',
      closedBody: "The balance of {{amount}} carried over to the next month. This month's lines cannot be added or changed.",
      addTitle: 'New line',
      editTitle: 'Change line {{what}}',
      lineRef: 'No. {{lp}} · {{date}}',
      chip: {
        expense: 'Expense',
        income: 'Income',
        loan: 'Loan',
        repayment: 'Loan repayment',
        funding: 'Funding',
        defunding: 'Funding return',
        deposit: 'Bank deposit',
        withdrawal: 'Bank withdrawal',
        move: 'Cash transfer',
      },
      flow: { out: 'Leaves the desk', in: 'Comes into the desk' },
      voucherNumber: 'The system gives the {{symbol}} number.',
      voucherNumbered: 'The system gave document {{number}}.',
      documentPlaceholder: 'e.g. FV/2026/513',
      counterpartyPlaceholder: 'e.g. ORLEN',
      lenderPlaceholder: 'e.g. Jan Kowalski',
      descriptionPlaceholder: 'What for, briefly',
      add: 'Add line',
      saveChanges: 'Save changes',
      why: {
        document: 'Type the document number: the line is not added without it.',
        amount: 'Type the amount.',
        counterparty: 'Type who lends the desk the cash, or who the desk pays it back to.',
        date: 'The date must be in {{month}}.',
        target: 'Pick the desk the cash goes to.',
      },
      flash: {
        added: 'Line added: {{what}}.',
        saved: 'Changes saved: {{what}}.',
        deleted: 'Line deleted: {{what}}.',
        dated: '{{what}} now stands under {{date}}.',
      },
      deleteTitle: 'Delete line {{what}}?',
      noCounterparty: 'No counterparty',
      deleteRises: 'The balance goes up by {{by}} to {{balance}}.',
      deleteFalls: 'The balance goes down by {{by}} to {{balance}}.',
      keep: 'Keep',
      linked: {
        title: {
          takings: 'Desk takings · {{name}}',
          in: 'Transfer from · {{name}}',
          out: 'Transfer to · {{name}}',
          payout: 'Payout · {{name}}',
          return: 'Payout return · {{name}}',
        },
        fromTransfer: 'Line from a transfer',
        fromPayroll: 'Line from payroll',
        payroll: 'Payroll',
        paid: 'Paid',
        returned: 'Return received',
        reportDate: 'Takings day',
        bag: 'Bag',
        sent: 'Sent',
        accepted: 'Received',
        date: 'Date in the main desk',
        transferHint: 'Changes only the day the transfer stands under in the book. The amount, the bag and the takings day are changed on the transfer.',
        payoutHint: 'Changes only the day the payout stands under in the book. The amounts are changed in payroll.',
        save: 'Save date',
      },
      toAccept_one: 'To accept · {{count}} handover',
      toAccept_other: 'To accept · {{count}} handovers',
      move: {
        pick: '— choose —',
        hint: 'Cash from this stash B desk to another stash B desk, or to a sales desk as its float.',
        to: 'To desk',
        groupMain: 'Another stash B desk — in both books at once',
        groupSales: 'Float — the desk accepts it where it arrives',
        hintMain: "A line of this book with its KW number; that stash B desk's book shows it the other way round at once.",
        hintSales: 'Float: the desk accepts the cash where it arrives; until then it is on its way.',
        numbered: 'The system gives the KW number.',
        send: 'Send the cash',
        sendAmount: 'Send {{amount}}',
        flash: 'Sent {{amount}} to {{desk}}.',
      },
    },
    payoutPart: {
      a: 'Pay A',
      b: 'Pay B',
      bonus: 'Bonus B',
      sc: 'Service Charge',
    },
    payouts: {
      payrollMonth: 'Payroll',
      previousMonth: 'Previous payroll month',
      nextMonth: 'Next payroll month',
      bookMonth: 'Lines go to the book of: {{month}}',
      tabs: 'Which payouts',
      tab: {
        todo: 'To settle · {{n}}',
        done: 'Paid · {{n}}',
      },
      nothingTodo: 'Nothing to settle. The ready payouts for {{month}} are paid.',
      nothingDone: 'Nothing paid yet.',
      employee: 'Employee',
      left: 'Left',
      flag: {
        notReady: 'No longer Ready',
        changed: 'Changed after payout',
        partly: 'Partly paid',
      },
      changedTitle: '{{part}}: paid {{paid}}, payroll now {{now}}',
      paidTitle: '{{part}}: paid {{date}} · {{number}}',
      paidMark: 'paid',
      returnBadge: 'Returned to the desk',
      undo: 'Undo',
      inBook: 'Open in the book',
      undoQuestion: 'Undo {{number}}?',
      flashUndone: 'Undid {{number}} ({{name}}). The line left the book.',
      back: 'To return to the desk',
      people_one: '{{count}} person',
      people_other: '{{count}} people',
      toPay: 'To pay',
      cashNow: 'In stash B now',
      cashShort: 'In stash B · not enough for all',
      waitingTitle: 'Waiting for “Ready” · {{n}}',
      waitingHint: 'They show up to pay once someone marks them in payroll.',
      openPayroll: 'Open payroll',
      changedTitleNote: 'Payroll changed after the payout',
      changedLine: '{{part}}: paid {{paid}} ({{number}}), now {{now}}.',
      changedTopUp: 'Top up {{amount}}.',
      changedBack: 'The employee returns {{amount}} to the desk.',
      partsLegend: 'What you settle now',
      paidOn: 'paid {{date}} · {{number}}',
      cannotPay: 'You may not pay out from this desk.',
      confirmPaid: 'Paid {{amount}}',
      confirmBack: 'Return of {{amount}} received',
      flashPaid: 'Paid {{amount}}: {{name}} · {{number}}.',
      flashBack: 'Return of {{amount}} received: {{name}} · {{number}}.',
      drawerTitle: 'Cash payouts',
      action: {
        pay: 'Pay',
        topUp: 'Top up',
        back: 'Return',
      },
    },
    overview: {
      title: 'Overview',
      subtitle: 'Each main desk’s balance at the end of every month of the year',
      previousYear: 'Previous year',
      nextYear: 'Next year',
      yearTitle: 'Year {{year}}',
      yearHint: 'Balance at month end. Click a month to see its detail below.',
      desk: 'Desk',
      stash: 'stash {{stash}}',
      subtotal: '{{company}} total',
      total: 'All desks total',
      yearEnd: 'Year end',
      inTransit: 'In transit now',
      monthTitle: 'Month: {{month}}',
      futureMonth: 'This month has not started yet.',
      opening: 'Opening',
      in: 'In',
      out: 'Out',
      closing: 'Closing',
    },
    settings: {
      floatTarget: 'Left after handover (target)',
      floatTargetHint: 'What stays in the drawer after the handover: 0 at the restaurant, a fixed float at the reception.',
      shareholder: 'Shareholder (loans)',
      shareholderHint: "Stash A's loans come from them only: their name stands as the counterparty of a loan and its repayment.",
      seriesPrinter: 'Printer',
      seriesPrinterFor: 'Printer for series {{series}}',
      noSeriesPrinter: '— not set —',
      seriesNoPrinterWarning: 'No printer — the VAT suggestion will not cover it.',
      title: 'Settings',
      subtitle: 'Cash desks, their data sources, series and payment types, printers and terminals, thresholds and book categories',
      readOnly: 'View only — a general manager or an administrator makes changes.',
      desksTitle: 'Cash desks',
      desksHint: 'A desk is deactivated, never deleted — its reports stay.',
      deskName: 'Name',
      dayStart: 'Day start',
      dayStartHint:
        'A document belongs to the day of its issue date; one issued before this time belongs to the day before.',
      openingCash: 'Opening cash',
      openingCashHint: 'The cash before the first report.',
      openingDate: 'First day',
      active: 'Active',
      activeFor: 'Active: {{name}}',
      position: 'Order',
      activate: 'Activate',
      deactivate: 'Deactivate',
      addDesk: 'Add desk',
      editDesk: 'Edit desk: {{name}}',
      deskTitle: 'Desk: {{name}}',
      deskHint: 'The data sources this desk takes documents from, each with its series and payment types; and its printers and terminals.',
      sourcesTitle: 'Data sources',
      sourcesHint: 'Per source: the series this desk settles, and what each payment type is to its drawer.',
      noDeskSources: 'The desk takes data from no source yet.',
      addSource: 'Add source',
      addSourceLabel: '— pick a source —',
      removeSource: 'Remove source',
      removeSourceQuestion: 'Remove {{name}} from the desk? Its series and payment types go from this desk.',
      seriesTitle: 'Document series',
      noSeries: 'No series.',
      seriesElsewhere: 'Bound to: {{desk}}',
      seriesMoves: 'Moves here from: {{desk}}',
      saveSeries: 'Save series',
      printersTitle: 'Fiscal printers',
      terminalsTitle: 'Terminals',
      deviceName: 'Name',
      printerModel: 'Model',
      uniqueNumber: 'Unique number',
      tid: 'TID',
      addPrinter: 'Add printer',
      addTerminal: 'Add terminal',
      paymentTypesTitle: 'Payment types',
      paymentTypesOf: 'Payment types: {{name}}',
      unmapped_one: '{{count}} payment type is not mapped.',
      unmapped_other: '{{count}} payment types are not mapped.',
      unmappedOption: '— not mapped —',
      noPaymentTypes: 'The source has sent no payment types yet.',
      paymentType: 'Code',
      paymentName: 'Name',
      paymentKind: 'To the desk',
      paymentKindFor: 'To the desk: {{type}}',
      thresholdsTitle: 'Check thresholds',
      thresholdsHint: 'In złoty.',
      threshold: {
        fiscalTolerance: 'Fiscal report tolerance',
        systemTolerance: 'Tolerance against the system',
        cashWarning: 'Cash difference — warning',
        cashError: 'Cash difference — error',
      },
      companiesTitle: 'Companies and main desks',
      companiesHint: 'The companies whose cash the main cashier keeps. Each has two main desks: A and B.',
      noMainDesks: 'No company has main desks yet.',
      companyName: 'Company',
      company: 'Company',
      companyOff: 'switched off',
      addMainDesks: 'Add main desks',
      addMainDesksHint: "Adds the company's two main desks: stash A and stash B. Companies are added in the Admin module.",
      everyCompanyHasMainDesks: 'Every company that is on has main desks. A new company is added in the Admin module.',
      companiesLink: 'Admin → Companies',
      noSources: 'No data source is on for the desk to add. Sources are added in the Admin module.',
      sourcesLink: 'Admin → Data sources',
      sourceOff: 'off — its series can only be unbound',
      salesDesksTitle: 'Sales desks',
      mainDesksTitle: 'Main desks',
      mainDesksHint: 'Main desks are added for a company, two each: A and B.',
      mainDeskLabel: '{{company}} — stash {{stash}}',
      stash: 'Stash',
      target: 'Hands cash to',
      targetHint: 'The main desk, of any company, this desk hands its cash to. The cashier does not pick it when sending.',
      noTarget: '— not set —',
    },
    checkShort: {
      FISCAL_NOT_VALID: 'Fiscal',
      TERMINAL_NOT_VALID: 'Terminal',
      SYSTEM_CASH_NOT_VALID: 'Cash ≠ izzy',
      SYSTEM_CARD_NOT_VALID: 'Card ≠ izzy',
      SYSTEM_TOTAL_NOT_VALID: 'Total ≠ izzy',
      SMALL_CASH_DIFF: 'Small difference',
      LARGE_CASH_DIFF: 'Large difference',
      PREVIOUS_REPORT_NOT_APPROVED: 'Previous not accepted',
      NO_PREVIOUS_REPORT: 'No previous',
      MISSING_REPORT: 'Missing day',
      FISCAL_DATE_MISMATCH: 'Print date',
      SYSTEM_DATA_STALE: 'Old izzy data',
      DOCUMENT_COUNTED_TWICE: 'Counted twice',
      FISCAL_MISSING: 'No fiscal',
      TERMINAL_MISSING: 'No terminal',
    },
    handovers: {
      report: {
        clear: 'Report clear',
        flagged: 'Report flagged',
        difference: 'cash difference {{amount}}',
        state: {
          draft: 'in progress',
          submitted: 'submitted, waiting for the manager',
          returned: 'returned for correction',
          accepted: 'accepted',
        },
        open: 'Open the report',
      },
      title: 'Accept takings',
      waiting: 'to accept: {{count}} · {{amount}}',
      allIn: 'all accepted',
      allInLong: 'Nothing else waits. The “To accept” tile leaves the book and comes back with the next handover.',
      day: 'day {{date}}',
      byHand: 'cash sent by hand',
      byHandBadge: 'By hand',
      settled: 'Settled just now',
      outcome: {
        accepted: 'accepted',
        returned: 'day returned for correction',
        refused: 'refused, the sender sends it again',
      },
      flash: '{{desk}}: {{what}}.',
      floats: 'Float on its way to the desks',
      floatLine: '{{desk}} · sent {{at}} · waits for the desk to accept it',
      footnote: 'Accepted takings go into the book with a KP number, under the day accepted. Change the date in the book with the pencil beside the line.',
    },
  },
  dataSources: {
    filter: 'Source',
    title: 'Data sources',
    gastronomyHint:
      'The Izzy Rest databases gastronomy reads. A source is added on Administration → Data sources; here you decide whether gastronomy derives it. The points of sale, series, groups and products below are one source’s.',
    hotelHint:
      'The Izzy Hotel databases the hotel reads — each one a property. A source is added on Administration → Data sources; here you decide whether the hotel derives it. The rooms, rents and blocks below are one source’s.',
    cateringHint:
      'The Izzy Rest databases of the canteens — each one a client site. A source is added on Administration → Data sources; here you decide whether catering derives it. The settlement reasons below are one source’s.',
    none: 'The module reads no source yet.',
    off: 'off',
    progress: '{{derived}} of {{staged}} days derived',
    failed_one: '{{count}} day failed',
    failed_other: '{{count}} days failed',
    lastPush: 'last push {{at}}',
    never: 'has sent nothing yet',
    remove: 'Take off the list',
    removeConfirm: 'The module stops reading {{name}}. What it derived stays, and is not shown. Take it off?',
    addLabel: 'Source to add',
    addPlaceholder: 'Pick a source',
    add: 'Add to the list',
    noCandidates: 'No other source of this kind — sources are added on Administration → Data sources.',
  },
  admin: {
    role: {
      admin: 'Administrator',
      hr_manager: 'HR manager',
      hr_administrator: 'HR administrator',
      hr_viewer: 'HR viewer',
      department_supervisor: 'Department supervisor',
      gastronomy_manager: 'Gastronomy manager',
      gastronomy_viewer: 'Gastronomy viewer',
      catering_manager: 'Catering manager',
      catering_site_manager: 'Canteen manager',
      catering_viewer: 'Catering viewer',
      hotel_manager: 'Hotel manager',
      hotel_viewer: 'Hotel viewer',
      cashier_admin: 'Cash desk administrator',
      cash_desk_manager: 'Cash desk manager',
      main_cashier: 'Main cashier',
      cashier: 'Cashier',
      cashier_viewer: 'Cash desk viewer',
    },
    permission: {
      cashierPayouts: 'Cash payouts from payroll',
      adminUsers: 'Users and roles',
      adminSettings: 'Shared settings: companies, data sources, weather',
      adminLogs: 'Application logs: errors, warnings, requests',
      hrRead: 'Read HR: employees, timesheet',
      hrEmployeesRead: 'Employee record: agreements, company, card',
      hrAttendanceCorrect: 'Correct attendance',
      hrAttendanceCorrectPast: 'Ended-month corrections',
      hrEmployeesWrite: 'Edit employees, agreements and cards',
      hrHistoryRead: "An employee's change history",
      hrCostsRead: 'Pay rates and costs',
      hrReportsRead: 'HR reports',
      hrPayrollRead: 'Read payroll',
      hrPayrollWrite: 'Edit and import payroll',
      hrPayrollClose: 'Close and reopen a payroll month',
      hrReferenceWrite: 'Reference data: departments, positions',
      hrRecalculate: 'Rebuild a month',
      hrReadersManage: 'Card readers',
      hotelRead: 'Read the hotel',
      hotelSettings: 'Hotel settings and night qualification',
      gastronomyRead: 'Read gastronomy',
      gastronomySettings: 'Gastronomy settings',
      gastronomyRemarks: "Sales' remarks in split variants",
      cateringRead: 'Catering view',
      cateringSettle: 'Catering monthly settlement',
      cateringSettings: 'Catering settings',
      cashierRead: 'Read cash desks',
      cashierReport: 'Day report and sending cash',
      cashierAccept: 'Accept reports and transfers',
      cashierMainDesk: 'Keep a main desk',
      cashierOverview: "Every main desk's cash",
      cashierSettings: 'Cash desk settings',
      cashierMonthReopen: 'Reopen a cash month',
      cashierAcceptOwn: 'Accept your own report, with no second person',
    },
    scope: {
      costCentre: 'Departments',
      revenueCentre: 'Gastronomy outlets',
      canteen: 'Canteens',
      cashDesk: 'Cash desks',
      allCostCentres: 'all departments',
      someCostCentres: 'chosen departments',
      allRevenueCentres: 'all gastronomy outlets',
      someRevenueCentres: 'chosen gastronomy outlets',
      allCanteens: 'all canteens',
      someCanteens: 'chosen canteens',
      allCashDesks: 'all cash desks',
      someCashDesks: 'chosen cash desks',
      noCostCentres: 'There are no departments yet.',
      noRevenueCentres: 'There are no gastronomy outlets yet.',
      noCanteens: 'There are no canteens yet.',
      noCashDesks: 'There are no cash desks yet.',
    },
    roles: {
      new: 'New role',
      newTitle: 'New role',
      editTitle: 'Role: {{name}}',
      viewTitle: 'System role: {{name}}',
      create: 'Create',
      name: 'Name',
      kind: 'Kind',
      system: 'system',
      custom: 'custom',
      scopes: 'Scope',
      holders: 'People',
      empty: 'No roles.',
      view: 'Show',
      systemDescription: 'A system role is defined in code. It cannot be changed or deleted.',
      editDescription: "Saving replaces the role's whole list of permissions.",
      editHeldDescription_one: '{{count}} person holds this role. Saving changes their access at once.',
      editHeldDescription_other: '{{count}} people hold this role. Saving changes their access at once.',
      heldBy_one: '{{count}} person holds it. Take it from them first.',
      heldBy_other: '{{count}} people hold it. Take it from them first.',
      confirmDelete: 'Delete the role {{name}}?',
      companyWide: 'company-wide',
      narrowedBy: 'narrowed: {{type}}',
      writes: 'changes data',
      everywhere: 'everywhere, no scope',
      marksHelp:
        '"Company-wide": the permission holds everywhere. "Narrowed": it holds only in the scope you choose when you give the role to a person.',
      pickAPermission: 'Tick at least one permission.',
    },
    companies: {
      subtitle: 'The companies that employ on agreements and payslips, and whose cash the main desks keep.',
      new: 'New company',
      newTitle: 'New company',
      editTitle: 'Company: {{name}}',
      dialogDescription: "A company's data is shared by HR, payroll and the cash desks.",
      create: 'Add',
      name: 'Name',
      taxId: 'Tax ID (NIP)',
      address: 'Address',
      optimaPayerName: 'Optima payer',
      optimaPayerNameHint:
        'The exact company line from an Optima payroll PDF. Links payroll imports to this company. Leave empty if unused.',
      position: 'Order',
      positionHint: 'A smaller number sits higher in lists.',
      positionInvalid: 'Enter a whole number, 0 or more.',
      active: 'On',
      activeHint: 'No main desks are added for a company that is off. The agreements, payslips and desks that name it stay.',
      off: 'off',
      usage: 'Used by',
      unused: 'unused',
      agreements_one: '{{count}} agreement',
      agreements_other: '{{count}} agreements',
      payrollEntries_one: '{{count}} payroll entry',
      payrollEntries_other: '{{count}} payroll entries',
      desks_one: '{{count}} main desk',
      desks_other: '{{count}} main desks',
      inUse: 'The company is in use. Switch it off instead of deleting it.',
      confirmDelete: 'Delete the company {{name}}?',
      empty: 'No companies.',
      notAvailable: 'Only somebody with "Shared settings" manages companies.',
    },
    logs: {
      subtitle: 'What the API wrote: errors, warnings and every request, for the last 30 days. Newest first.',
      level: 'Kind',
      levelAll: 'Everything',
      levelError: 'Errors',
      levelWarn: 'Warnings',
      levelRequest: 'Requests',
      badgeError: 'Error',
      badgeWarn: 'Warning',
      badgeRequest: 'Request',
      status: 'Response',
      statusAny: 'Any',
      status4xx: 'Caller error (4xx)',
      status5xx: 'Server error (5xx)',
      searchPlaceholder: 'Message, path, person',
      to: 'Up to',
      time: 'Time',
      where: 'Where',
      statusCode: 'Status',
      duration: 'Took',
      durationValue: '{{ms}} ms',
      user: 'Person',
      message: 'Message',
      context: 'Context',
      stack: 'Stack trace',
      requestId: 'Request id',
      showRequest: 'Show this request',
      requestFilter: 'Request {{id}}',
      clearRequest: 'Show every line',
      older: 'Load older',
      empty: 'No lines.',
      noMatch: 'No line matches the filters.',
      notAvailable: 'Only somebody with "Application logs" sees the logs.',
    },
    sources: {
      subtitle: 'Where data enters Overseer: each izzy database, its token and its last push.',
      new: 'New source',
      newTitle: 'New data source',
      editTitle: 'Source: {{name}}',
      dialogDescription:
        'The kind says what the source sends: an izzy database, or a site’s card readings. Any number of sources of a kind may be on — each one’s data is kept apart.',
      create: 'Add',
      name: 'Name',
      kind: 'Kind',
      kindRest: 'Izzy Rest',
      kindHotel: 'Izzy Hotel',
      kindCardReader: 'Card readers',
      active: 'On',
      activeHint: 'The collector of a source switched off sends nothing: its token is refused.',
      state: 'State',
      on: 'on',
      off: 'off',
      switchOn: 'Switch on',
      switchOff: 'Switch off',
      switchOffConfirm: 'This source’s collector stops being accepted. Switch it off?',
      token: 'Token',
      tokenSetAt: 'issued {{at}}',
      tokenNone: 'no token',
      lastPush: 'Last push',
      lastPushNever: 'nothing yet',
      lastPushStale: '{{at}} — over 24 h ago',
      collectorVersion: 'Collector version',
      tokenIssue: 'Issue a token',
      tokenRotate: 'Issue a new token',
      tokenRotateConfirm:
        'The previous token stops working immediately, and the collector sends nothing until the new value is in its config. Issue it?',
      issuedFor: 'New token: {{name}}',
      tokenOnce: 'Save it now — it cannot be shown again.',
      tokenCopied: 'Saved',
      usage: 'Used by',
      unused: 'unused',
      desks_one: '{{count}} cash desk',
      desks_other: '{{count}} cash desks',
      stagedDays_one: '{{count}} day from the collector',
      stagedDays_other: '{{count}} days from the collector',
      devices_one: '{{count}} card reader',
      devices_other: '{{count}} card readers',
      derivedBy: 'Read by',
      derivedByNone: 'no module — staged only',
      derivedByHint: 'Which module derives a source is set on that module’s settings.',
      inUse: 'Cash desks take data from this source, card readers belong to it, or its collector has sent data. Switch it off instead.',
      confirmDelete: 'Delete source {{name}}?',
      empty: 'No data sources.',
      notAvailable: 'Only somebody with "Shared settings" manages data sources.',
    },
    users: {
      person: 'Person',
      email: 'Email address',
      you: 'this is you',
      access: 'Roles',
      noRoles: 'no roles',
      assignmentLine: '{{role}}: {{where}}',
      modules: 'Applications',
      allModules: 'all (Administrator)',
      lastSeen: 'Last sign-in',
      neverSignedIn: 'not yet',
      invitationPending: 'invitation pending',
      account: 'Account',
      active: 'Active',
      disabled: 'Disabled',
      empty: 'No users.',
      noMatch: 'Nobody matches the search.',
      searchPlaceholder: 'Name or email address',
      notAvailable: 'Only an Administrator manages users.',
      invite: 'Invite',
      inviting: 'Inviting…',
      inviteTitle: 'Invite a user',
      inviteDescription:
        'Cognito emails this person a temporary password. You will not see that password.',
      add: 'Add',
      adding: 'Adding…',
      addTitle: 'Add a user',
      addDescription:
        'This server sends no invitations. The person must already have an account; the access applies at their first sign-in.',
      edit: 'Edit',
      editTitle: 'Access: {{name}}',
      editDescription: "Saving replaces this person's whole set of roles.",
      displayName: 'Display name',
      displayNameHint: 'Shown instead of the email address. Empty: the email address.',
      roles: 'Roles',
      generalRoles: 'General',
      scopeOf: 'Scope of {{role}}: {{type}}',
      history: 'Data history',
      historyOf: 'Data history of {{role}}',
      historyUnlimited: 'no limit',
      historyLimitedBefore: 'current month +',
      historyLimitedAfter: 'months back',
      historyMonths: 'Months back',
      historyEvery: 'All data, from the start.',
      historySummary: 'history: current month + {{months}}',
      scopeList: '{{names}} ({{type}})',
      inactiveDesk: 'inactive',
      mainDesk: 'main desk',
      deletedCanteen: 'deleted',
      chooseScope: 'Choose: all or chosen.',
      pickAtLeastOne: 'Choose at least one.',
      noModules: 'none — this person signs in and sees nothing',
      modulesFromRoles: "They follow from the ticked roles' permissions.",
      selfWarning: 'This is your account. The server will not let you remove your own Administrator role.',
      resend: 'Resend invitation',
      resent: 'Invitation sent again.',
      disable: 'Disable',
      enable: 'Enable',
      confirmDisable: 'Disable the account of {{name}}? This person will no longer sign in.',
      confirmEnable: 'Enable the account of {{name}}?',
      confirmDisableAccessOnly:
        'Disable the access of {{name}}? The Cognito sign-in stays on, but this person will see nothing.',
    },
  },
  /** Weather, shown beside the gastronomy figures and set on Administracja → Pogoda. */
  weather: {
    condition_clear: 'Clear',
    condition_partly: 'Partly cloudy',
    condition_cloudy: 'Cloudy',
    condition_fog: 'Fog',
    condition_drizzle: 'Drizzle',
    condition_rain: 'Rain',
    condition_snow: 'Snow',
    condition_showers: 'Showers',
    condition_thunderstorm: 'Thunderstorm',
    settingsNav: 'Weather',
    settingsTitle: 'Weather location',
    settingsSubtitle: 'The place the weather shown beside the gastronomy figures is measured for',
    locationName: 'Place name',
    latitude: 'Latitude',
    longitude: 'Longitude',
    latitudeHint: 'Between -90 and 90, e.g. 52.23',
    longitudeHint: 'Between -180 and 180, e.g. 21.01',
    latitudeRange: 'Enter a number between -90 and 90.',
    longitudeRange: 'Enter a number between -180 and 180.',
    nameRequired: 'Enter a place name.',
    noLocation: 'No location has been set yet — no weather is shown beside the gastronomy figures.',
    saved: 'Weather location saved.',
    adminOnly: 'Only somebody with "Shared settings" manages the weather location.',
  },
  period: {
    thisMonth: 'This month',
    lastMonth: 'Last month',
    last90: 'Last 90 days',
    ytd: 'Year to date',
    from: 'From',
    to: 'To',
    prevMonth: 'A month earlier',
    nextMonth: 'A month later',
    date: 'Day',
    prevDay: 'Previous day',
    nextDay: 'Next day',
    toComparable: 'Go to the comparable day a year before: {{date}}',
    toComparableAhead: 'Go to the day this one is the comparable day of: {{date}}',
  },
  common: {
    salesLines: {
      product: 'Product',
      group: 'Group',
      quantity: 'Quantity',
      price: 'Price',
      vat: 'VAT',
      value: 'Value',
      cancelled: 'cancelled',
    },
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    cancel: 'Cancel',
    dismiss: 'Dismiss',
    save: 'Save',
    saving: 'Saving…',
    edit: 'Edit',
    delete: 'Delete',
    actions: 'Actions',
    search: 'Search',
    clear: 'Clear',
    range: '{{from}}–{{to}} of {{total}}',
    // Cognito reports no total, so there is nothing to say "of" against. Only
    // the number of rows on this page.
    previousPage: 'Previous page',
    nextPage: 'Next page',
    loading: 'Loading…',
    retry: 'Try again',
    never: 'Never',
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    error: 'Something went wrong',
    notFound: 'Page not found',
    notFoundDescription: 'This address does not exist.',
    backToDashboard: 'Back to the dashboard',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    language: 'Language',
    skipToContent: 'Skip to content',
    networkError: 'Could not reach the server. Check your connection and try again.',
    forbidden: 'You have no permission for this data or this action.',
    forbiddenBecause: 'No permission. {{reason}}',
    offline: 'You are offline — changes will not be saved.',
  },
} as const;
